// Dublin, "Soft rain on the Liffey". A wet October evening on the Liffey quays.
//
// Where we stand: at the south end of the Millennium footbridge, where it meets Wellington Quay, a little west
// (upstream) of the Ha'penny Bridge, looking east down the river and slightly across it, from the height of someone
// standing on the footbridge. The distance to the Ha'penny Bridge is drawn shorter than it is; nothing is rearranged.
// Left to right: the Spire's needle over the roofs of Bachelors Walk, the north quay's Georgian fronts running away
// behind the bridge with the boardwalk hung on the quay wall, the Ha'penny Bridge itself (white cast iron, one low
// arch, lattice railings, its three lamp arches seen more and more edge-on towards our side), and down the river
// O'Connell Bridge with its lamps, Liberty Hall's slab, the green copper dome of the Custom House behind the
// Loopline railway bridge, the docklands cranes and O'Connell Bridge House over the roofs of Aston Quay. On the
// right our own quay runs away from us: the granite quay wall with its lamp standard and a herring gull on its
// coping, the wet footpath and road of Wellington Quay, a pub, the Merchants' Hall with Merchant's Arch (the way into
// Temple Bar) facing the bridge, and the fronts of Crampton Quay beyond.
// Not in view from here: Trinity College and the Guinness brewery (behind us and to the right), the Four Courts
// (upstream, behind us), and the Samuel Beckett Bridge, which lies downstream beyond the Custom House.
//
// The city's billboard is a rooftop hoarding on Bachelors Walk, turned to face the traffic coming down the quays.
// render(t, { crowd }) thins the people (CROWD); crowd 0 draws nobody (see "People").
//
// The loop's timeline (seconds; every period divides 240):
//   event                                          time            period
//   bus along Bachelors Walk (stops, moves on)     6-44, 118-156, 196-234   three a loop
//   bus over O'Connell Bridge (far, glimpsed under the arch)   48-74 (east), 164-190 (west)
//   DART over the Loopline bridge (far)            24-40, 144-160  120 s
//   taxi along Bachelors Walk                      84-104
//   pedestrian lights at both bridge ends          a 60 s cycle    60 s
//   people over the Ha'penny Bridge                all loop long   80 or 120 s each
//   cyclist down Wellington Quay                   66-90
//   delivery bike up Wellington Quay               150-172
//   river boat down the Liffey, under the arch     92-150  (gentle moment 2; 'boat' at 93)
//   cormorant dives and comes up                   every 40 s      40 s
//   Christ Church bell, the gulls lift off         132, 135.5, 139; gulls 132-170  (gentle moment 3)
//   the gull on our coping cries                   100, and 134.4 as the others lift
//   the moon comes out of its veil                 14-84           (gentle moment 1, with the first bus)
//   a lone gull crosses the sky                    18-34, 214-232
//   pub door opens, a patron steps out             every 60 s      60 s
//   the man at the quay wall lifts his cup         every 24 s      24 s
//   quay lamp sputters                             58.4, 203.2     (the hoarding's right lamp at 76.4)
//   aeroplane on approach, far                     170-226
// The longest gap between two timed things is about 12 s.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, text, textWidth, FONT3, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= The camera =================
   Everything is placed in metres and projected, so the perspective holds together:
   a = east along the river from where we stand, c = north from the south quay wall, z = up from the water. */
const TH = 8 * Math.PI / 180, CT = Math.cos(TH), ST = Math.sin(TH);   // we are turned 8 degrees towards the north bank
const F = 330, CXP = 272, HZ = 150;                   // focal length (px), the lens axis' column, the horizon's row
const EYE = 6.4, CAMC = 1.5;                          // eye above the water (we stand on the footbridge), and north of the south quay wall
const RW = 44;                                        // the river's width: the north quay wall stands at c = RW
const QZ = 4.4, CAPZ = QZ + 1;                        // quay (street) level, top of the quay walls' parapets
const AB = 46, BW = 1.83, RISE = 3.35;                // the Ha'penny Bridge: where it crosses, half its width, its rise
const CNF = RW + 13, CSF = -12;                       // the house fronts of the north and the south quay
const A_OC = 206;                                     // O'Connell Bridge (drawn nearer than it is)
const PH = 1.85;                                      // a pixel person's height in metres (a little tall, so they read)
/** screen [x, y, depth] of a point */
function proj(a, c, z) {
  const dc = c - CAMC, d = a * CT + dc * ST;
  return [CXP + F * (a * ST - dc * CT) / d, HZ - F * (z - EYE) / d, d];
}
/** how far down the river (a) screen column x meets the vertical plane at c */
const aAt = (x, c) => { const q = (x - CXP) / F, dc = c - CAMC; return dc * (CT + q * ST) / (ST - q * CT); };
const deckZ = c => { const u = (c - RW / 2) / (RW / 2); return QZ + 0.3 + RISE * Math.pow(Math.max(0, 1 - u * u), 0.8); };
const VPX = Math.round(CXP + F * ST / CT);            // the river's vanishing point (column), on the horizon

/* ================= Layout (screen) ================= */
const BILL = { x: 84, y: 64, w: 46, h: 20 };          // face of the rooftop hoarding on Bachelors Walk (the city's billboard)
const BILL_LAMPS = [BILL.x + 8, BILL.x + 23, BILL.x + 38], BILL_LAMP_Y = BILL.y - 8;
const MOON = { x: 204, y: 36 };
const SPIRE = { x: 52, tip: 54 };                     // the Spire, over the roofs at the bridge's north end
const LAMP_A = 18;                                    // the near lamp standard on our quay wall
// The share of the people the scene shows by itself: 1 = everyone, about half by default, 0 = nobody.
const CROWD = 0.5;
const rankOf = (i, off = 0) => frac((i + 0.5) * 0.6180339887 + off);

/* ================= The loop's timeline (seconds) ================= */
const QBUS = [{ t0: 6, t1: 44, live: 0 }, { t0: 118, t1: 156, live: 1 }, { t0: 196, t1: 234, live: 0 }];   // along Bachelors Walk
const TAXI = { t0: 84, t1: 104 };
const OBUS = [{ t0: 48, t1: 74, dir: 1, live: 1 }, { t0: 164, t1: 190, dir: -1, live: 0 }];               // over O'Connell Bridge
const DART = [[24, 40, 1], [144, 160, -1]];
const BOAT = { t0: 92, t1: 150 };
const BELLS = [132, 135.5, 139];
const FLOCK = { t0: 132, t1: 170 };
const BIKE = { t0: 66, t1: 90 }, COURIER = { t0: 150, t1: 172 };
const PLANE = { t0: 170, t1: 226 };
const LAMP_FLICK = [[58.4, 59.8], [203.2, 204.3]];
const DOOR_T = 60, CUP_T = 24, DIVE_T = 40;

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const mixc = (A, B, t) => [A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t];
const toHex = v => '#' + ((1 << 24) | (clamp(Math.round(v[0]), 0, 255) << 16) | (clamp(Math.round(v[1]), 0, 255) << 8) | clamp(Math.round(v[2]), 0, 255)).toString(16).slice(1);
const mixHex = (a, b, t) => toHex(mixc(C(a), C(b), t));
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(Math.round(x1) - Math.round(x0)), Math.abs(Math.round(y1) - Math.round(y0)), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    /** a straight line in the world, projected (mir: its mirror image in the water) */
    line3(A, B, col, mir = false) {
      const p = proj(A[0], A[1], mir ? -A[2] : A[2]), q = proj(B[0], B[1], mir ? -B[2] : B[2]);
      const n = Math.max(1, Math.ceil(Math.max(Math.abs(q[0] - p[0]), Math.abs(q[1] - p[1])) * 1.5));
      for (let k = 0; k <= n; k++) {
        const u = k / n, s = proj(lerp(A[0], B[0], u), lerp(A[1], B[1], u), (mir ? -1 : 1) * lerp(A[2], B[2], u));
        this.set(s[0], s[1], col);
      }
    },
    text(s, i, j, col) { x.putImageData(img, 0, 0); text(x, s, i, j, col); d.set(x.getImageData(0, 0, w, h).data); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered seam) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** crop a painter to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const R = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(x, y, w, h); };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
/** a sprite from rows of palette keys ('.' or an unknown key = transparent) */
function sprite(rows, key, flip = false) {
  const w = Math.max(...rows.map(r => r.length)), p = pix(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) p.set(flip ? w - 1 - i : i, j, key[row[i]]); });
  return p.done();
}
/** flattened dithered ellipse of light (pools on wet ground), drawn additively */
function makePool(rx, ry, col, maxA) {
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = hex(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
const flick = (t, list, id) => {
  let v = 1;
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); v = hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.15; }
  return v;
};


/* ================= Sky: a wet night, blue-grey above, the city's sodium glow down the river ================= */
const SKY = ['#080b18', '#0a0e1e', '#0d1224', '#10162a', '#141b32', '#19203a', '#1f2742', '#262e4a', '#2f3550', '#3a3c56',
  '#46435a', '#534b5c', '#60545c', '#6c5c5a'].map(hex);
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 168), 1.25) * 0.62;
    v += Math.max(0, 1 - Math.hypot((i - VPX) / 300, (y - 158) / 105)) * 0.4;         // the city's glow down the river
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 110, (y - MOON.y) / 64)) * 0.2;    // the moonlit break
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
function renderMoon() {
  const r = 6, s = r * 2 + 1, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    let col = dx + dy < -4 ? '#f2f2f6' : '#d8dae6';
    if (dd > r - 1.2) col = dx + dy > 0 ? '#9ea4c0' : '#c4c8dc';
    if (Math.hypot(dx + 2, dy + 1) < 1.3 || Math.hypot(dx - 2, dy - 2) < 1.1 || Math.hypot(dx - 1, dy + 3) < 0.9) col = '#b4b8cc';
    p.set(i, y, col);
  }
  return p.done();
}
const STARS = [];
{ const r = rng(5); for (let i = 0; i < 54; i++) STARS.push({ x: Math.floor(r() * W), y: Math.floor(r() * 104), k: 40 + Math.floor(r() * 90), ph: r(), b: 0.35 + r() * 0.65 }); }

/* ----- low rain clouds, lit from below by the city: lighter than the night in the breaks between them ----- */
const CLOUD_DEFS = [
  { seed: 11, y: -10, h: 60, banks: 5, k: 1, pal: { top: '#3c4460', body: '#262d46', core: '#1e243a', bot: '#3e3c50', bot2: '#2c3046' } },
  { seed: 23, y: 22, h: 62, banks: 6, k: 2, pal: { top: '#4c5672', body: '#2f3650', core: '#262c44', bot: '#5c5058', bot2: '#3e3c50' } },
  { seed: 37, y: 62, h: 66, banks: 6, k: 3, pal: { top: '#56607c', body: '#363c56', core: '#2c324a', bot: '#706062', bot2: '#544c58' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 56 + r() * 84, base = h * (0.5 + r() * 0.3);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 11 + r() * 19, ry = 4 + r() * 6.5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.35;
      if (v > best) best = v;
    }
    best += 0.16 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  // no outline: the edge thins out into the sky in an ordered dither, the underside warms over a few rows in the city's
  // glow, and the top catches a little moonlight
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = D[y * W + x];
    if (v <= -0.26 + bayer(x, y) * 0.56) continue;
    let db = 7, dt = 4;
    for (let k = 1; k < 7; k++) if (get(x, y + k) <= 0.03) { db = k; break; }
    for (let k = 1; k < 4; k++) if (get(x, y - k) <= 0.03) { dt = k; break; }
    const base = pick([P.body, P.core], smooth(0.35, 0.85, v), x, y), under = (7 - db) / 6, lit = (4 - dt) / 4;
    p.set(x, y, under >= lit ? pick([base, P.bot2, P.bot], under * 0.74, x, y) : pick([base, P.top], lit * 0.62, x, y));
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Far down the river (drawn larger than life, in their true order) ================= */
// From the left: the Spire over the north-side roofs; then, round the river's vanishing point, Liberty Hall, the
// Custom House dome behind the Loopline railway bridge, O'Connell Bridge in front of them, the docklands cranes and
// O'Connell Bridge House on the south side.
const OCB = { x0: 251, x1: 321, deck: 153, wl: 160 };  // O'Connell Bridge on screen
const OCB_LAMPS = [254, 274, 286, 298, 310, 319];
const LOOP_Y = 145;                                    // the Loopline bridge's girder
const LIB = { x0: 281, x1: 292, top: 106 }, DOME = { cx: 307, top: 124 }, OCH = { x0: 350, x1: 362, top: 106 };
const CRANES = [{ x: 329, top: 121, j0: 315, j1: 337 }, { x: 339, top: 129, j0: 332, j1: 345 }];
const FAR_WINS = [];
function renderFar(p) {
  const r = rng(41);
  const addWin = (x, y, col) => FAR_WINS.push({ id: FAR_WINS.length, x, y, col, n: 2 + Math.floor(r() * 6), p: 0.3 + r() * 0.4, off: r() });
  // --- the Spire: a stainless steel needle, its top lit from within
  const SP = ['#3e4864', '#5a6684', '#8490ac', '#b6c0d6'];
  for (let y = SPIRE.tip; y < 112; y++) {
    const k = y - SPIRE.tip, w = k < 16 ? 1 : k < 40 ? 2 : 3;
    for (let j = 0; j < w; j++) p.set(SPIRE.x - (w >> 1) + j, y, k < 7 ? '#eef4ff' : k < 10 ? '#c4d0ea' : SP[Math.min(3, (j === 0 ? 2 : 0) + (k < 34 ? 1 : 0))]);
  }
  // --- docklands far behind: two slim towers and the cranes
  for (const [x0, w, top] of [[311, 4, 134], [320, 5, 130]]) {
    p.fill(x0, top, w, 150 - top, '#262c44'); p.fill(x0, top, 1, 150 - top, '#343c58');
    for (let y = top + 2; y < 144; y += 2) for (let x = x0 + 1; x < x0 + w; x++) if (r() < 0.35) addWin(x, y, '#a89068');
  }
  for (const c of CRANES) {
    p.fill(c.x, c.top, 1, 150 - c.top, '#2a3048'); p.fill(c.j0, c.top, c.j1 - c.j0, 1, '#343c58');
    for (let x = c.j0; x < c.j1; x += 2) p.set(x, c.top + 1, '#262c44');
    p.fill(c.x - 1, c.top - 3, 3, 3, '#2a3048'); p.set(c.j0 + 3, c.top + 2, '#262c44'); p.set(c.j0 + 3, c.top + 3, '#262c44');
  }
  // --- Liberty Hall: the slab with its crinkled roof
  for (let y = LIB.top; y < 151; y++) for (let x = LIB.x0; x <= LIB.x1; x++) {
    let col = x === LIB.x0 ? '#46506e' : x >= LIB.x1 - 1 ? '#262c44' : '#343c58';
    if ((y - LIB.top) % 3 === 2 && x > LIB.x0 && x < LIB.x1) col = '#2a3048';
    p.set(x, y, col);
  }
  for (let x = LIB.x0 - 1; x <= LIB.x1 + 1; x++) { p.set(x, LIB.top - 1 - ((x - LIB.x0) & 1), '#5a6684'); p.set(x, LIB.top, '#1e2338'); }
  p.fill(LIB.x0 + 3, LIB.top - 5, 4, 3, '#2a3048'); p.fill(LIB.x0 + 3, LIB.top - 5, 4, 1, '#46506e');
  for (let y = LIB.top + 3; y < 143; y += 3) for (let x = LIB.x0 + 1; x < LIB.x1 - 1; x++) if (r() < 0.4) addWin(x, y, r() < 0.7 ? '#c8a878' : '#a8b4cc');
  // --- the Custom House: its long Portland stone front, the drum and the green copper dome, floodlit
  for (let y = DOME.top + 13; y < 151; y++) for (let x = DOME.cx - 15; x <= DOME.cx + 15; x++)
    p.set(x, y, y === DOME.top + 13 ? '#b8b4a4' : Math.abs(x - DOME.cx) < 5 ? ((x & 1) ? '#c8c2ae' : '#7a7a80') : (y % 3 === 0 ? '#8a8a8c' : '#9a988e'));
  p.fill(DOME.cx - 5, DOME.top + 11, 11, 2, '#c8c2ae');                                         // the portico's pediment
  for (let y = DOME.top + 6; y < DOME.top + 11; y++) for (let x = DOME.cx - 3; x <= DOME.cx + 3; x++) p.set(x, y, (x & 1) ? '#b0ac9c' : '#6e6e78');   // the drum's columns
  p.fill(DOME.cx - 4, DOME.top + 5, 9, 1, '#c8c2ae');
  for (let y = DOME.top; y < DOME.top + 5; y++) {
    const hw = Math.round(Math.sqrt(1 - Math.pow((DOME.top + 4.5 - y) / 5.2, 2)) * 4);
    for (let x = -hw; x <= hw; x++) p.set(DOME.cx + x, y, x < -1 ? '#86c8ac' : x <= 1 ? '#5ea088' : '#3c6e62');
  }
  p.set(DOME.cx, DOME.top - 1, '#a8b0a0'); p.set(DOME.cx, DOME.top - 2, '#c8c2ae'); p.set(DOME.cx, DOME.top - 3, '#8a9088');   // lantern and the statue of Commerce
  // --- the Loopline railway bridge in front of it: a dark lattice girder on piers
  for (let x = 266; x <= 327; x++) {
    p.set(x, LOOP_Y - 1, '#3a4260'); p.set(x, LOOP_Y, (x & 1) ? '#141828' : '#262c44'); p.set(x, LOOP_Y + 1, '#1a1f32');
  }
  for (const x of [279, 297, 315]) p.fill(x, LOOP_Y + 2, 2, 8, '#20263a');
  // --- far quays between the two bridges: low fronts with a few lights
  for (let x = 262; x <= 334; x++) {
    if (x > 284 && x < 318) continue;
    const top = 144 + Math.floor(hash(x >> 2, 5, 9) * 4);
    for (let y = top; y < 154; y++) p.set(x, y, y === top ? '#3a4260' : '#232940');
    if (r() < 0.4) addWin(x, top + 2 + Math.floor(r() * 4), '#c89a60');
  }
  // --- O'Connell Bridge: three low arches of granite, a balustrade, lamp standards
  for (let x = OCB.x0; x <= OCB.x1; x++) {
    p.set(x, OCB.deck - 2, (x % 3 === 0) ? '#9aa0b4' : '#6e7690'); p.set(x, OCB.deck - 1, '#8a90a6'); p.set(x, OCB.deck, '#565e7a');
    const m = (x - OCB.x0 - 2) % 23, pier = m < 0 || m >= 20 || x > OCB.x1 - 2;
    for (let y = OCB.deck + 1; y <= OCB.wl; y++) {
      const open = !pier && (y - OCB.deck) > 1 + 3.2 * Math.pow(Math.abs(m - 9.5) / 10, 2.4);
      p.set(x, y, pier ? (y === OCB.deck + 1 ? '#7a8098' : '#4a5270') : open ? '#0c1020' : '#3c4460');
    }
  }
  for (const x of OCB_LAMPS) { p.fill(x, OCB.deck - 6, 1, 4, '#1c2136'); p.set(x - 1, OCB.deck - 6, '#1c2136'); p.set(x + 1, OCB.deck - 6, '#1c2136'); }
  // --- O'Connell Bridge House over the roofs of the south quays, a lit sign box on its roof
  for (let y = OCH.top; y < 136; y++) for (let x = OCH.x0; x <= OCH.x1; x++) {
    let col = x === OCH.x0 ? '#465070' : x === OCH.x1 ? '#222840' : '#303854';
    if ((y - OCH.top) % 3 === 1 && x > OCH.x0 && x < OCH.x1) col = '#20263c';
    p.set(x, y, col);
  }
  for (let y = OCH.top + 1; y < 128; y += 3) for (let x = OCH.x0 + 1; x < OCH.x1; x++) if (r() < 0.45) addWin(x, y, r() < 0.6 ? '#c8a878' : '#9aa8c8');
  p.fill(OCH.x0 + 1, OCH.top - 6, OCH.x1 - OCH.x0 - 1, 5, '#141828'); p.fill(OCH.x0 + 3, OCH.top - 1, 1, 1, '#20263c'); p.fill(OCH.x1 - 3, OCH.top - 1, 1, 1, '#20263c');
  const c = pix(W, H); c.d.set(p.d);
  return trim(c);
}

/* ================= River mist: one ordered-dithered bank that drifts and breathes (as London's fog does) ================= */
const FOG = { seed: 6, y: 116, h: 74, k: 1, top: '#566282', bot: '#8a8a98', alpha: 0.5,
  dens: t => 0.52 + 0.24 * wave(t, 3, 0.15) + 0.08 * wave(t, 7, 0.6) };
const FOG_LEVELS = 8;
function makeFog(def) {
  const h = def.h, n = W * h, Fd = new Float32Array(n), A = hex(def.top), B = hex(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.6);
    const nz = 0.48 + 0.34 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.05) + 0.16 * Math.sin(TAU * 5 * x / W - y * 0.19 + def.seed * 2)
      + 0.1 * Math.sin(TAU * 11 * x / W + y * 0.41) + 0.06 * Math.sin(TAU * 23 * x / W - y * 0.7);
    Fd[y * W + x] = prof * clamp(nz, 0, 1);
  }
  const levels = [];
  for (let k = 0; k <= FOG_LEVELS; k++) {
    const D = k / FOG_LEVELS * 1.25, p = pix(W, h);
    for (let q = 0; q < n; q++) {
      const x = q % W, y = (q / W) | 0, a = Math.floor(Math.min(1, Fd[q] * D) * 4 + bayer(x, y)) / 4;
      if (a <= 0) continue;
      p.set(x, y, mixc(A, B, y / (h - 1)), Math.round(Math.min(1, a) * 255));
    }
    levels.push(p.done());
  }
  return { ...def, levels };
}


/* ================= The quays: house fronts, streets, granite quay walls, the boardwalk =================
   Painted by casting a ray through every pixel onto the planes of the two quays (walls, parapet caps, streets, house
   fronts), so both sides run away to the river's vanishing point in one perspective. */
const A_FAR = 208;                                    // beyond this the far skyline's own drawing takes over
const BWALK = { z: QZ - 0.3, a0: 52, a1: 204, w: 3 }; // the Liffey boardwalk, hung outside the north quay wall
const SHOPH = 3.7, FLH = [3.2, 2.9, 2.6, 2.3], WINH = [1.95, 1.7, 1.35, 1.1];
const BRICKS = ['#4c2c30', '#3e2c32', '#584438', '#4a3230', '#3c3038', '#523630'].map(hex);
const PAINTS = ['#666a80', '#7c7466', '#74565e', '#546678', '#667466', '#7a6a58'].map(hex);
const SHOPS = ['#a82c30', '#1e6a48', '#28489a', '#c89a28', '#20222e', '#7a2c6a', '#cfc6b0', '#187a80', '#b8521e'].map(hex);
const LITS = ['#ffd890', '#f6b868', '#ffe8c0', '#e8c8a0', '#ffcf80', '#a8c0e0'].map(hex);
const HAZE = hex('#303a58'), WARM = hex('#d89858'), DARKGLASS = hex('#141a2c'), STONE = hex('#8a8a96');
const WINS = [];                                      // every window that switches: { id, n, p, off, side, x0, y0, x1, y1 }
function makeHouse(r, a0, w, o = {}) {
  const up = o.up ?? (r() < 0.5 ? 4 : 3), bays = o.bays ?? (w < 6.4 ? 2 : 3), painted = r() < 0.4;
  const s = { a0, a1: a0 + w, up, bays, kind: 'house', chim: r() < 0.75, open: r() < 0.62, lettered: r() < 0.7,
    wall: painted ? PAINTS[Math.floor(r() * PAINTS.length)] : BRICKS[Math.floor(r() * BRICKS.length)],
    shop: SHOPS[Math.floor(r() * SHOPS.length)], wins: [], ...o };
  s.top = SHOPH + FLH.slice(0, up).reduce((x, y) => x + y, 0) + 0.6;
  let fb = SHOPH;
  for (let f = 0; f < up; f++) {
    for (let b = 0; b < bays; b++) {
      const uc = (b + 0.5) * w / bays, v = r(), mode = v < 0.2 ? 1 : v < 0.52 ? 2 : 0;
      const wn = { u0: uc - 0.55, u1: uc + 0.55, h0: fb + 0.75, h1: fb + 0.75 + WINH[f], mode, col: LITS[Math.floor(r() * LITS.length)], id: -1 };
      if (mode === 2) { wn.id = WINS.length; WINS.push({ id: wn.id, n: 2 + Math.floor(r() * 5), p: 0.35 + r() * 0.3, off: r(), x0: W, y0: H, x1: -1, y1: -1 }); }
      s.wins.push(wn);
    }
    fb += FLH[f];
  }
  return s;
}
function makeRow(seed, a0, a1, fixed = [], opt = () => ({})) {
  const r = rng(seed), out = [...fixed];
  let a = fixed.length ? fixed[fixed.length - 1].a1 : a0;
  while (a < a1 - 3) { const w = Math.min(5.6 + r() * 3.4, a1 - a); out.push(makeHouse(r, a, w, opt(a, w))); a += w; }
  return out;
}
const R0 = rng(77);
const PUB = makeHouse(R0, 30.5, 9.9, { up: 3, bays: 3, wall: hex('#4a2a2e'), shop: hex('#143c2c'), open: true, lettered: true, kind: 'pub', chim: true });
const HALL = { a0: 40.6, a1: 50.4, top: 11, kind: 'hall', wins: [], chim: false };
const HALL_WIN = { id: WINS.length };
WINS.push({ id: HALL_WIN.id, n: 3, p: 0.6, off: 0.3, x0: W, y0: H, x1: -1, y1: -1 });
const BAR = makeHouse(R0, 50.4, 6.8, { up: 3, bays: 2, wall: hex('#7c7466'), shop: hex('#a8242c'), open: true, lettered: true, kind: 'bar', chim: true });
const S_HOUSES = makeRow(78, 0, 202, [PUB, HALL, BAR]);
const N_HOUSES = makeRow(79, 49.2, 203, [], (a, w) => (a + w > 68 && a < 95 ? { up: 4, chim: false } : {}));   // the hoarding stands on four-storey roofs
N_HOUSES[2].shop = hex('#28489a'); N_HOUSES[2].open = true; N_HOUSES[2].books = true;   // the bookshop (BOOKS is lettered on its fascia)
N_HOUSES[4].shop = hex('#1e6a48'); N_HOUSES[4].open = true; N_HOUSES[4].pubN = true;    // a pub on Bachelors Walk
const houseAt = (Hs, a) => { for (const s of Hs) if (a >= s.a0 && a < s.a1) return s; return null; };
// quay lamps stand every 22 m on both walls; their light on walls, streets and fronts
const lampNear = (a, a0) => { const m = (((a - a0) % 22) + 22) % 22, d = Math.min(m, 22 - m); return Math.exp(-(d * d) / 44); };
const N_LAMP0 = 58, S_LAMP0 = LAMP_A;
const dith = (v, n, x, y) => Math.floor(clamp(v, 0, 1) * n + 0.5 + (bayer(x, y) - 0.5) * 0.36) / n;   // flat bands, a narrow dithered seam

let MC = [0, 0, 0], ML = null, MW = -1;               // the material's colour, its lit variant (switching windows), that window's id
/** the Merchants' Hall: granite, three round arches below (the middle one is Merchant's Arch), three round-headed windows above */
function hallMat(u, h, x, y, g) {
  const cen = [1.75, 4.9, 8.05];
  let col = (Math.floor(h / 0.62) & 1) ? [86, 92, 112] : [94, 100, 120];
  if (frac(h / 0.62) < 0.16 || frac((u + (Math.floor(h / 0.62) & 1) * 0.7) / 1.5) < 0.08) col = [58, 62, 82];
  ML = null;
  for (let k = 0; k < 3; k++) {
    const du = Math.abs(u - cen[k]);
    if (h < 4.2 && du < 1.25 && (h < 2.9 || du * du + (h - 2.9) * (h - 2.9) < 1.56)) {       // ground-floor arches
      if (k === 1) col = h < 0.5 ? [200, 130, 70] : mixc([255, 214, 140], [214, 132, 70], clamp(h / 4, 0, 1));   // the lit passage
      else col = h > 2.9 ? (frac(du / 0.5) < 0.2 ? [40, 40, 52] : [120, 96, 70]) : du < 0.1 || h < 0.4 ? [16, 18, 28] : [34, 30, 38];
      if (du > 1.05 || (h >= 2.9 && du * du + (h - 2.9) * (h - 2.9) > 1.1)) col = [150, 152, 164];     // dressed surround
    }
    if (h > 5.9 && du < 0.8 && (h < 8.5 || du * du + (h - 8.5) * (h - 8.5) < 0.64)) {         // upper windows
      const bar = du < 0.08 || Math.abs(h - 7.3) < 0.1;
      col = bar ? [70, 74, 92] : DARKGLASS;
      if (k === 2) { MW = HALL_WIN.id; ML = bar ? [120, 96, 70] : [246, 196, 120]; }
      if (k === 0 && !bar) col = [232, 184, 110];
    }
  }
  if (h > 4.5 && h < 4.9) col = [136, 140, 156];
  if (h > 10.1) col = h > 10.6 ? [112, 116, 134] : [150, 152, 166];
  const lit = 0.55 + 0.45 * dith(clamp(1 - h / 9, 0, 1) * (0.5 + 0.5 * g), 4, x, y);
  if (col[0] < 200) col = mixc([col[0] * lit, col[1] * lit, col[2] * lit], WARM, 0.22 * g * clamp(1 - h / 7, 0, 1));
  MC = col;
  return ML ? 2 : 1;
}
/** a house front at u metres along it and h metres above the street; x, y: the screen pixel (for dithering) */
function frontMat(s, u, h, x, y, g, d) {
  ML = null;
  if (s.kind === 'hall') return h > s.top ? 0 : hallMat(u, h, x, y, g);
  const w = s.a1 - s.a0;
  if (h > s.top) {                                    // chimney stack and pots on the party wall
    if (!s.chim || u > 1.3 || h > s.top + 1.9) return 0;
    if (h > s.top + 1.4) { if (frac(u / 0.45) > 0.5) return 0; MC = [112, 62, 50]; return 1; }
    MC = u < 0.18 ? [70, 52, 56] : [50, 36, 40];
    return 1;
  }
  const street = dith(clamp(1 - h / 10, 0, 1) * (0.45 + 0.55 * g), 5, x, y);                  // light from the street, fading up the front
  let col;
  if (h > s.top - 0.55) col = h > s.top - 0.18 ? [128, 130, 146] : [98, 100, 118];            // parapet coping
  else if (h < SHOPH) {                               // the painted shopfront
    const sc = s.shop, lit = s.open;
    if (h >= 3.0) {
      col = h > 3.56 ? mixc(sc, [255, 255, 255], 0.35) : sc;
      if (s.lettered && h > 3.14 && h < 3.5 && u > 0.9 && u < w - 0.9 && hash(Math.floor(u * 2.6), s.a0 | 0, 7) < 0.62) col = [226, 190, 110];
    } else if (u < 0.36 || u > w - 0.36) col = mixc(sc, [255, 255, 255], 0.18);
    else if (u > w - 1.75 && u < w - 0.75 && h < 2.9) {                                        // the door, a lit light over it
      col = h > 2.35 ? (lit ? [255, 220, 150] : [40, 44, 62]) : (u > w - 1.55 && u < w - 0.95 && h > 1.1 && lit ? [240, 180, 100] : mixc(sc, [0, 0, 0], 0.45));
    } else if (u > 0.7 && u < w - 2.1 && h > 0.75 && h < 2.9) {                                // the shop window
      const mull = frac((u - 0.7) / 1.25) < 0.1 || Math.abs(h - 2.4) < 0.06;
      if (lit) col = mull ? mixc(sc, [0, 0, 0], 0.3) : h > 2.4 ? [255, 236, 180] : pick([[255, 222, 150], [250, 196, 116], [232, 160, 84]], 1 - (h - 0.75) / 1.7, x, y, 0.1);
      else col = mull ? mixc(sc, [0, 0, 0], 0.4) : [30, 36, 56];
    } else col = mixc(sc, [0, 0, 0], h < 0.75 ? 0.4 : 0.15);
    if (col[0] < 200 || col[2] < 90) { const k = 0.55 + 0.45 * street; if (!(lit && col[0] > 220)) col = [col[0] * k, col[1] * k, col[2] * k]; }
    MC = col;
    return 1;
  } else {
    let wn = null;
    for (const q of s.wins) if (u >= q.u0 - 0.14 && u < q.u1 + 0.14 && h >= q.h0 - 0.2 && h < q.h1 + 0.12) { wn = q; break; }
    const inGlass = wn && u >= wn.u0 && u < wn.u1 && h >= wn.h0 && h < wn.h1;
    if (inGlass) {
      const bar = Math.abs(u - (wn.u0 + wn.u1) / 2) < 0.07 || Math.abs(h - (wn.h0 + wn.h1) / 2) < 0.07;
      const litC = bar ? mixc(wn.col, [60, 40, 30], 0.45) : h > (wn.h0 + wn.h1) / 2 ? wn.col : mixc(wn.col, [200, 120, 60], 0.3);
      const drk = bar ? [58, 62, 84] : h > (wn.h0 + wn.h1) / 2 ? [26, 34, 56] : DARKGLASS;
      MC = wn.mode === 1 ? litC : drk;
      if (wn.mode === 2) { ML = litC; MW = wn.id; return 2; }
      return 1;
    }
    if (wn && h < wn.h0) col = [120, 122, 138];                                               // the sill
    else if (wn) col = mixc(s.wall, [0, 0, 0], 0.3);                                          // the reveal
    else {
      col = s.wall;
      if (d < 70 && frac(h / 0.32) < 0.22) col = mixc(col, [0, 0, 0], 0.16);
      if (u < 0.14) col = mixc(col, [0, 0, 0], 0.35);
    }
    const k = 0.5 + 0.5 * street;
    col = mixc([col[0] * k, col[1] * k, col[2] * k], WARM, 0.3 * g * clamp(1 - (h - 2) / 8, 0, 1) * street);
  }
  MC = col;
  return 1;
}
/** granite quay wall: z above the water, a along it */
function wallMat(a, z, x, y, g) {
  const row = Math.floor(z / 0.5), bed = frac(z / 0.5) < 0.2, joint = bed || frac((a + (row & 1) * 0.8) / 1.6) < 0.09;
  let col = [[40, 46, 68], [46, 53, 76], [52, 60, 84]][Math.floor(hash(Math.floor((a + (row & 1) * 0.8) / 1.6), row, 3) * 3)];
  if (joint) col = bed && z > 1.5 && z < 4.6 && hash(Math.floor(a * 2.5), row, 13) < 0.16 ? [42, 54, 48] : [24, 28, 44];   // moss along a few bed joints
  const run = hash(Math.floor(a / 0.35), 5, 11);
  if (run < 0.3 && z > CAPZ - 0.6 - run * 6) col = mixc(col, [16, 20, 34], 0.26);                    // rain run off the coping
  if (z < 1.5) col = mixc(col, z < 0.45 ? [10, 16, 20] : [24, 44, 40], z < 0.45 ? 0.75 : 0.5 + 0.3 * hash(Math.floor(a * 3), row, 9));   // weed and wet below the tide line
  const lit = dith(g * clamp((z - 2.2) / 3, 0, 1), 4, x, y);
  return mixc(col, [150, 116, 84], 0.42 * lit);
}
/** the parapets' granite coping: long stones with joints across them, the river-side arris catching the light;
    cc: metres in from the river side */
function capMat(a, cc, x, y, g) {
  const u = frac(a / 1.3);
  let col = hash(Math.floor(a / 1.3), 7, 3) < 0.5 ? [112, 120, 142] : [102, 110, 132];
  if (u < 0.04 || u > 0.985) col = [64, 70, 92];
  else if (cc < 0.04) col = [138, 144, 164];
  else if (hash(x, y, 77) < 0.1) col = [col[0] - 14, col[1] - 14, col[2] - 12];                    // the granite's grain
  return mixc(col, [226, 180, 124], 0.6 * dith(g, 4, x, y));
}
/** wet street and footpaths; c0: the river-side edge of the paving, dir: +1 north quay, -1 south quay */
function groundMat(a, cc, x, y, g, north) {
  // cc: metres from the quay wall towards the house fronts
  const wd = north ? CNF - RW : -CSF;
  let col, sheen = 0;
  if (cc < 2.9) {                                     // footpath along the wall: granite flags
    col = (frac(a / 0.9) < 0.1 || frac(cc / 0.95) < 0.1) ? [30, 34, 52] : [48, 54, 76];
  } else if (cc < 3.15) col = [92, 98, 120];          // kerb
  else if (cc < wd - 3.1) {                           // the roadway, wet tarmac
    col = [24, 28, 42]; sheen = 0.5;
    if (Math.abs(cc - 4.5) < 0.09) col = [86, 92, 112];                                       // cycle lane line
    if (Math.abs(cc - (wd - 3.1 + 4.5) / 2 - 0.3) < 0.09 && frac(a / 4) < 0.5) col = [92, 98, 118];   // lane dashes
  } else if (cc < wd - 2.85) col = [92, 98, 120];
  else col = (frac(a / 0.9) < 0.1 || frac((cc - wd) / 0.95) < 0.1) ? [34, 36, 52] : [54, 58, 78];
  const shopGlow = clamp(1 - (wd - cc) / 6, 0, 1), lamp = g * clamp(1 - cc / 9, 0, 1);
  const v = dith(Math.max(lamp, shopGlow * 0.8) * (0.7 + sheen), 5, x, y);
  return mixc(col, [196, 142, 86], 0.5 * v);
}


/* ----- the ray through a pixel: what it meets first (direct view), or what the water mirrors there ----- */
const HIT = { k: 0, a: 0, c: 0, z: 0, d: 0 };
const K_WATER = 1, K_NWALL = 2, K_NCAP = 3, K_NSTREET = 4, K_NFRONT = 5, K_SWALL = 6, K_SCAP = 7, K_SGROUND = 8, K_SFRONT = 9, K_DECK = 10;
function cast(x, y, mirror) {
  const q = (x + 0.5 - CXP) / F, da = CT + q * ST, dc = ST - q * CT;
  let dz = (HZ - (y + 0.5)) / F, z0 = EYE;
  const sWat = dz < 0 ? -EYE / dz : Infinity;
  if (mirror) { z0 = -EYE; dz = -dz; }                // past the water the ray carries on as its mirror image
  const hit = (k, s) => { HIT.k = k; HIT.d = s; HIT.a = s * da; HIT.c = CAMC + s * dc; HIT.z = z0 + s * dz; return k; };
  const north = dc > 1e-6, south = dc < -1e-6;
  if (!north && !south) return !mirror && sWat * da < A_FAR ? hit(K_WATER, sWat) : 0;
  const cw = north ? RW : 0, sWall = (cw - CAMC) / dc;
  if (!mirror && sWat < sWall) {
    if (north) {                                      // the boardwalk's deck, before the water under it
      const sB = (BWALK.z - EYE) / dz, cB = CAMC + sB * dc, aB = sB * da;
      if (cB >= RW - BWALK.w && aB >= BWALK.a0 && aB <= BWALK.a1) return hit(K_DECK, sB);
    }
    return sWat * da < A_FAR ? hit(K_WATER, sWat) : 0;
  }
  if (sWall * da > A_FAR) return 0;
  if (mirror && north) {                              // the boardwalk's underside, seen in the water
    const sB = (BWALK.z + EYE) / dz, cB = CAMC + sB * dc, aB = sB * da;
    if (sB > sWat && cB >= RW - BWALK.w && cB <= RW && aB >= BWALK.a0 && aB <= BWALK.a1) return hit(K_DECK, sB);
  }
  const zW = z0 + sWall * dz;
  if (zW < 0) return 0;
  if (zW <= CAPZ) return hit(north ? K_NWALL : K_SWALL, sWall);
  if (!mirror && dz < 0) {                            // over the parapet: its cap, then the paving behind it
    const sC = (CAPZ - EYE) / dz, cC = CAMC + sC * dc;
    if (north ? cC <= RW + 0.45 : cC >= -0.45) return hit(north ? K_NCAP : K_SCAP, sC);
    const sG = (QZ - EYE) / dz, cG = CAMC + sG * dc;
    if (north ? cG < CNF : cG > CSF) return sG * da < A_FAR ? hit(north ? K_NSTREET : K_SGROUND, sG) : 0;
  }
  const sF = ((north ? CNF : CSF) - CAMC) / dc;
  if (sF * da > A_FAR) return 0;
  return hit(north ? K_NFRONT : K_SFRONT, sF);
}
const skyAt = (i, y) => {
  let v = Math.pow(Math.min(1, Math.max(0, y) / 168), 1.25) * 0.62;
  v += Math.max(0, 1 - Math.hypot((i - VPX) / 300, (y - 158) / 105)) * 0.4;
  return SKY[Math.round(clamp(v, 0, 1) * (SKY.length - 1))];
};
const WATER_Y0 = 153;                                 // first row that can be water
const RIVER_INK = [10, 16, 32];                       // the Liffey's own colour, that every mirrored quay is cooled towards
let WMASK;                                            // 1 where the frame shows the river
function renderQuays(farP) {
  const N = pix(W, H), NW = pix(W, H), S = pix(W, H), SW = pix(W, H), NL = pix(W, H), SL = pix(W, H), M = pix(W, H), WB = pix(W, H);
  WMASK = new Uint8Array(W * H);
  const MQ = new Float32Array(W * H * 3), MQN = new Uint8Array(W * H);   // the quays' mirror image, before it is softened
  const LIT = Array.from({ length: W }, () => ({ n: 0, y0: H, y1: -1, r: 0, g: 0, b: 0 }));   // per column: the lights mirrored there
  const WPAL = ['#1c2640', '#151d32', '#0f1626', '#0b111e', '#090e18'].map(hex);
  const bbox = (id, x, y) => { const w = WINS[id]; if (x < w.x0) w.x0 = x; if (x > w.x1) w.x1 = x; if (y < w.y0) w.y0 = y; if (y > w.y1) w.y1 = y; };
  const fogAt = d => clamp(1 - Math.exp(-Math.max(0, d - 36) / 190), 0, 1) * 0.8;   // the wet air between us and things: none up close
  const hazed = (col, d, x, y, k = 1) => mixc(col, HAZE, k < 1 ? fogAt(d) * k : dith(fogAt(d), 8, x, y));
  const shade = (x, y, mirror) => {                  // the colour of what cast() found (null: nothing, the sky)
    const { k, a, c, z, d } = HIT, north = k <= K_NFRONT || k === K_DECK;
    const g = lampNear(a, north ? N_LAMP0 : S_LAMP0);
    if (k === K_NWALL || k === K_SWALL) {
      let col = wallMat(a, z, x, y, g);
      if (north && a >= BWALK.a0 && a <= BWALK.a1 && z < BWALK.z && z > BWALK.z - 1.3) col = mixc(col, [8, 10, 18], 0.6);   // under the boardwalk
      return hazed(col, d, x, y);
    }
    if (k === K_NCAP || k === K_SCAP) return hazed(capMat(a, north ? c - RW : -c, x, y, g), d, x, y);
    if (k === K_DECK) {
      if (mirror) return [10, 12, 20];
      const plank = hash(Math.floor(a / 0.45), 3, 5), col = mixc(plank < 0.5 ? [70, 60, 56] : [58, 50, 48], [200, 150, 96], 0.45 * dith(g, 4, x, y));
      return hazed(col, d, x, y);
    }
    if (k === K_NSTREET) return hazed(groundMat(a, c - RW, x, y, g, true), d, x, y);
    if (k === K_SGROUND) return hazed(groundMat(a, -c, x, y, g, false), d, x, y);
    const s = houseAt(north ? N_HOUSES : S_HOUSES, a);
    if (!s) return null;
    const r = frontMat(s, a - s.a0, z - QZ, x, y, g, d);
    if (!r) return null;
    const bright = MC[0] > 200;
    if (r === 2 && !mirror) { (north ? NL : SL).set(x, y, hazed(ML, d, x, y, 0.5)); bbox(MW, x, y); WINS[MW].north = north; }
    return hazed(MC, d, x, y, bright ? 0.5 : 1);
  };
  for (let y = 24; y < H; y++) for (let x = 0; x < W; x++) {
    const k = cast(x, y, false);
    if (!k) continue;
    if (k === K_WATER) {
      WMASK[y * W + x] = 1;
      WB.set(x, y, pick(WPAL, (y - WATER_Y0) / 104, x, y));
      let col = null;
      if (cast(x, y, true)) col = shade(x, y, true);
      if (col) {
        const o = y * W + x;
        if (col[0] * 0.3 + col[1] * 0.59 + col[2] * 0.11 > 140 || (col[0] > 150 && col[0] > col[2] + 40)) {
          const L = LIT[x];                            // a lit window or shop: on the river it lies as a streak (STREAKS), not a picture
          L.n++; L.y0 = Math.min(L.y0, y); L.y1 = Math.max(L.y1, y); L.r += col[0]; L.g += col[1]; L.b += col[2];
          col = [col[0] * 0.42, col[1] * 0.32, col[2] * 0.22];
        }
        MQ[o * 3] = col[0]; MQ[o * 3 + 1] = col[1]; MQ[o * 3 + 2] = col[2]; MQN[o] = 1;
      } else {                                         // the far skyline's drawing, mirrored at its own waterline, or the sky
        const fy = 2 * OCB.wl - y, f = fy > 60 && farP.alpha(x, fy) ? farP.get(x, fy) : null;
        if (f) M.set(x, y, [f[0] * 0.75, f[1] * 0.75, f[2] * 0.8]);
        else { const sk = skyAt(x, 2 * HZ - y); M.set(x, y, [sk[0] * 0.62, sk[1] * 0.64, sk[2] * 0.7], 200); }
      }
      continue;
    }
    const col = shade(x, y, false);
    if (col) (k === K_NSTREET || k === K_NFRONT ? N : k < K_SWALL || k === K_DECK ? NW : k < K_SGROUND ? SW : S).set(x, y, col);
  }
  // the quays in the river: drawn down by the ripples into soft vertical smears (no brick or window detail), darker and
  // cooler than the quays themselves, and ordered-dithered
  for (let y = WATER_Y0; y < H; y++) for (let x = 0; x < W; x++) {
    const o = y * W + x;
    if (!MQN[o]) continue;
    let r = 0, g = 0, b = 0, n = 0;
    for (let j = -3; j <= 3; j++) for (let i = -1; i <= 1; i++) {
      const xx = x + i, yy = y + j, q = yy * W + xx;
      if (xx < 0 || xx >= W || yy >= H || !MQN[q]) continue;
      r += MQ[q * 3]; g += MQ[q * 3 + 1]; b += MQ[q * 3 + 2]; n++;
    }
    const v = mixc([r / n * 0.64, g / n * 0.66, b / n * 0.74], RIVER_INK, 0.26), th = bayer(x, y);
    M.set(x, y, [Math.floor(v[0] / 5 + th) * 5, Math.floor(v[1] / 5 + th) * 5, Math.floor(v[2] / 5 + th) * 5]);
  }
  return { N, NW, S, SW, NL, SL, M, WB, LIT };
}

/* ----- a filled quadrilateral in the world (corners in order), projected; fn(x, y, u, v) gives each pixel's colour ----- */
function quad3(p, A, B, Cc, D, fn) {
  const P4 = [A, B, Cc, D].map(q => proj(q[0], q[1], q[2]));
  const xs = P4.map(q => q[0]), ys = P4.map(q => q[1]);
  const x0 = Math.floor(Math.min(...xs)), x1 = Math.ceil(Math.max(...xs)), y0 = Math.floor(Math.min(...ys)), y1 = Math.ceil(Math.max(...ys));
  const cr = (ax, ay, bx, by, px, py) => (bx - ax) * (py - ay) - (by - ay) * (px - ax);
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
    let pos = 0, neg = 0;
    for (let k = 0; k < 4; k++) { const v = cr(xs[k], ys[k], xs[(k + 1) & 3], ys[(k + 1) & 3], x + 0.5, y + 0.5); if (v > 0) pos++; else if (v < 0) neg++; }
    if (pos && neg) continue;
    const col = fn(x, y, (x - x0) / Math.max(1, x1 - x0), (y - y0) / Math.max(1, y1 - y0));
    if (col) p.set(x, y, col);
  }
}


/* ================= The Ha'penny Bridge (1816): white cast iron, one low arch of 43 m, lattice railings, three lamp arches =================
   Built in the world and projected, so the deck narrows towards the north bank and the lamp arches, which span the
   deck, open up the further they stand from our side: a post at our end, a narrow arch at the far one. */
const IRON = { hi: '#f4f6fa', l: '#d2d9e6', m: '#a2acc4', s: '#6c7692', d: '#465070' };
const ARCH_C = [5.5, 22, 38.5];                       // where the three lamp arches stand along the deck
const RAILH = 1.1, ARCH_H = 2.7;
const ribDepth = c => 0.62 + 0.3 * Math.pow(Math.abs(c - RW / 2) / (RW / 2), 2);
const BR_LAMPS = ARCH_C.map(c => { const p = proj(AB, c, deckZ(c) + RAILH + ARCH_H - 0.45); return { x: Math.round(p[0]), y: Math.round(p[1]), c }; });
/** the bridge's edge at a (AB - BW: our side, AB + BW: the far one): for every screen column, the rows of the rail and the rib */
function edgeCols(a, mir) {
  const cols = [], sg = mir ? -1 : 1;
  const xa = proj(a, 0, 0)[0], xb = proj(a, RW, 0)[0];
  for (let x = Math.ceil(Math.min(xa, xb)); x <= Math.floor(Math.max(xa, xb)); x++) {
    const q = (x - CXP) / F, c = CAMC + a * (ST - q * CT) / (CT + q * ST);
    if (c < 0 || c > RW) continue;
    const zd = deckZ(c), d = a * CT + (c - CAMC) * ST, Y = z => Math.round(HZ - F * (sg * z - EYE) / d);
    cols.push({ x, c, rail: Y(zd + RAILH), deck: Y(zd), rib: Y(zd - ribDepth(c)) });
  }
  return cols;
}
function paintBridge(back, front, mir) {
  const sg = mir ? -1 : 1, dim = mir ? 0.62 : 1;
  const tone = h => { const v = C(h); return [v[0] * dim, v[1] * dim, v[2] * (mir ? 0.7 : 1)]; };
  const span = (p, x, ya, yb, fn) => { for (let y = Math.min(ya, yb); y <= Math.max(ya, yb); y++) { const col = fn(y); if (col) p.set(x, y, col); } };
  // the far edge: its rib and the top of its railing, dimmer (its lattice would only muddy ours)
  for (const e of edgeCols(AB + BW, mir)) {
    span(back, e.x, e.deck, e.rib, y => (y === e.deck || y === e.rib) ? tone(IRON.s) : null);
    span(back, e.x, e.rail, e.deck, y => y === e.rail ? tone(IRON.m) : frac(e.c / 2.2) < 0.07 ? tone(IRON.s) : null);
  }
  // the deck between the two edges, seen from a little above: wet, warm under the three lanterns
  if (!mir) for (let c = 0.1; c < RW; c += 0.035) {
    const g = Math.max(...ARCH_C.map(lc => Math.exp(-Math.pow((c - lc) / 3.4, 2))));
    back.line3([AB - BW, c, deckZ(c) + 0.02], [AB + BW, c, deckZ(c) + 0.02], mixc([96, 104, 128], [226, 190, 140], 0.7 * g));
  }
  // our edge: the rib (two chords and an open web), the deck's edge, the lattice railing
  for (const e of edgeCols(AB - BW, mir)) {
    const dn = e.rib >= e.deck ? 1 : -1;                // the way down on screen (up, in the mirror image)
    span(front, e.x, e.deck, e.rib, y => {
      if (y === e.rib) return tone(IRON.m);
      if (y === e.deck || y === e.deck + dn) return tone(y === e.deck ? IRON.hi : IRON.l);   // the deck's edge, a solid band
      return (frac(e.c / 0.9) < 0.16 || (e.x + y * 2) % 8 === 0 || (e.x - y * 2 + 800) % 8 === 0) ? tone(IRON.s) : tone(IRON.d);
    });
    span(front, e.x, e.rail, e.deck - dn, y => {
      if (y === e.rail) return tone(IRON.hi);
      if (frac(e.c / 2.2) < 0.07) return tone(IRON.hi);                                     // a standard every 2.2 m
      return (((e.x + y) & 3) === 0 || ((e.x - y) & 3) === 0) ? tone(IRON.l) : null;
    });
  }
  // the three lamp arches: two legs rising from the railings, bowed in to a lantern over the middle of the deck
  for (const c of ARCH_C) {
    const z0 = deckZ(c) + RAILH;
    let prev = null;
    for (let k = 0; k <= 40; k++) {
      const th = Math.PI * k / 40, pt = [AB - BW * Math.cos(th), c, z0 + ARCH_H * Math.pow(Math.sin(th), 0.55)];
      if (prev) (k <= 20 ? front : back).line3(prev, pt, tone(k <= 20 ? IRON.hi : IRON.m), mir);
      prev = pt;
    }
    for (const a of [AB - BW, AB + BW]) (a < AB ? front : back).line3([a, c, deckZ(c)], [a, c, z0], tone(a < AB ? IRON.hi : IRON.m), mir);
    const L = proj(AB, c, sg * (z0 + ARCH_H - 0.45)), lx = Math.round(L[0]), ly = Math.round(L[1]);
    front.set(lx, ly - sg, tone(IRON.s)); front.set(lx, ly, mir ? [200, 170, 110] : [255, 244, 204]); front.set(lx, ly + sg, mir ? [180, 140, 90] : [255, 214, 140]);
    if (!mir) { front.set(lx - 1, ly, [255, 226, 160]); front.set(lx + 1, ly, [255, 226, 160]); }
  }
}

/* ================= Things standing on the quays (projected): lamp standards, the boardwalk's rail, trees, signs ================= */
const N_LAMPS = [], S_LAMPS = [];                      // lantern positions on screen: { x, y, d } (halos, streaks, lit rain)
const SIG = { n: proj(49.6, RW + 0.7, QZ + 3), s: proj(43.6, -2.7, QZ + 3) };   // the pedestrian lights at the two ends of the bridge
const BAR_SIGN = { a: 51.4 }, PUB_SIGN = { a: 33.4 };
const OUTFALL = { x: Math.round(proj(15, 0, 1.7)[0]), y: Math.round(proj(15, 0, 1.7)[1]), wl: Math.round(proj(15, 0, 0)[1]) };
const TREES = [{ a: 91, c: RW + 2.3 }, { a: 135, c: RW + 2.3 }];
function lampPost(p, a, c, h, big) {
  const b = proj(a, c, CAPZ), t = proj(a, c, CAPZ + h), x = Math.round(b[0]), y0 = Math.round(t[1]), y1 = Math.round(b[1]);
  for (let y = y0 + 1; y <= y1; y++) p.set(x, y, '#12141e');
  if (big > 0) {                                      // a lantern you can read: cap, lit glass, a little crossbar under it
    p.set(x, y0 - 2, '#12141e'); p.fill(x - 1, y0 - 1, 3, 1, '#12141e'); p.fill(x - 1, y0, 3, 2, '#ffe9b4'); p.set(x, y0 + 1, '#ffd078');
    p.fill(x - 2, y0 + 3, 5, 1, '#12141e');
  } else { p.set(x, y0, '#ffe9b4'); p.set(x, y0 - 1, '#12141e'); }
  return { x, y: y0 + (big > 0 ? 1 : 0), d: b[2], a };
}
function bladeSign(p, lit, a, c0, z0, word, bg, ink, glow) {  // a vertical projecting sign, letters stacked; faces up the quay, towards us
  const tl = proj(a, c0, z0), x = Math.round(tl[0]), y = Math.round(tl[1]), h = word.length * 6 + 1;
  for (let j = y - 1; j <= y + h; j++) for (let i = x - 1; i <= x + 5; i++) lit.set(i, j, [0, 0, 0], 0);   // no window lights up through it
  p.fill(x - 1, y - 1, 7, h + 2, '#10121c'); p.fill(x, y, 5, h, bg);
  [...word].forEach((ch, k) => p.text(ch, x + 1, y + 1 + k * 6, ink));
  return { x: x + 2, y: y + (h >> 1), h, glow };
}
let BLADES = [];
function decorateQuays(N, NW, S, SW, NL, SL) {
  const r = rng(91);
  // --- north quay: lamp standards on the wall, the boardwalk's rail and brackets, two plane trees, a bus shelter
  for (let a = N_LAMP0; a < 200; a += 22) N_LAMPS.push(lampPost(NW, a, RW + 0.22, 4.2, a < 110 ? 1 : 0));
  for (let a = BWALK.a0; a <= BWALK.a1; a += 0.5) {
    const top = proj(a, RW - BWALK.w, BWALK.z + 1.1), ft = proj(a, RW - BWALK.w, BWALK.z);
    NW.set(top[0], top[1], '#7e88a4');
    if (top[2] < 120) NW.set(top[0], (top[1] + ft[1]) / 2, '#4a5470');
    NW.set(ft[0], ft[1] + 1, '#20222e');
    if (Math.round(a * 2) % 6 === 0) for (let y = Math.round(top[1]); y <= Math.round(ft[1]); y++) NW.set(top[0], y, '#59627e');
  }
  for (let a = BWALK.a0 + 2; a < 150; a += 6) NW.line3([a, RW - BWALK.w + 0.4, BWALK.z - 0.15], [a, RW, BWALK.z - 1.5], '#0c0e18');
  for (const tr of TREES) {
    const b = proj(tr.a, tr.c, QZ), cr = proj(tr.a, tr.c, QZ + 5.4), sc = F / b[2];
    for (let y = Math.round(cr[1]); y <= Math.round(b[1]); y++) NW.set(b[0], y, '#1a1418');
    const rx = 2.6 * sc, ry = 2.2 * sc;
    for (let y = -Math.ceil(ry); y <= Math.ceil(ry); y++) for (let x = -Math.ceil(rx); x <= Math.ceil(rx); x++) {
      const v = 1 - (x * x) / (rx * rx) - (y * y) / (ry * ry) + (r() - 0.5) * 0.5;
      if (v <= 0) continue;
      NW.set(cr[0] + x, cr[1] + y, y > ry * 0.2 ? (r() < 0.5 ? '#a8642a' : '#7a4622') : v > 0.5 ? '#3a2420' : (r() < 0.3 ? '#6a3c20' : '#4a2c20'));
    }
    tr.x = Math.round(cr[0]); tr.y = Math.round(cr[1]); tr.rx = rx; tr.ry = ry;
  }
  quad3(N, [96, CNF - 2.6, QZ + 2.5], [100.5, CNF - 2.6, QZ + 2.5], [100.5, CNF - 2.6, QZ], [96, CNF - 2.6, QZ], (x, y, u, v) => v < 0.25 ? [236, 240, 250] : (x & 1) ? [70, 84, 112] : [54, 64, 90]);
  // --- the pedestrian lights' poles at the bridge's two ends (the lamps are lit per frame)
  for (const [q, P] of [[SIG.n, NW], [SIG.s, S]]) { const ft = Math.round(q[1] + 3 * F / q[2]); for (let y = Math.round(q[1]); y <= ft; y++) P.set(q[0], y, '#10121c'); P.fill(Math.round(q[0]) - 1, Math.round(q[1]) - 1, 3, 4, '#10121c'); }
  // --- projecting signs on Bachelors Walk: the bookshop's and a pub's
  const nb = N_HOUSES[2], np = N_HOUSES[4];
  BLADES = [
    bladeSign(N, NL, nb.a0 + 0.3, CNF - 0.2, QZ + 10.9, 'BOOKS', '#28489a', '#f2ead2', '#7a9ae0'),
    bladeSign(N, NL, np.a0 + 0.3, CNF - 0.2, QZ + 9.2, 'PUB', '#1e6a48', '#f0d48a', '#6ad0a0'),
  ];
  // --- south quay: lamp standards down the wall (the nearest is drawn large, see renderNearLamp), bollards, signs
  for (let a = S_LAMP0 + 22; a < 200; a += 22) S_LAMPS.push(lampPost(SW, a, -0.22, 4.2, a < 100 ? 1 : 0));
  for (let a = 23; a < 78; a += 4.4) {                 // cast-iron bollards along the kerb, a gilt band round each
    if (Math.abs(a - 44.5) < 3) continue;
    const b = proj(a, -3.05, QZ), tp = proj(a, -3.05, QZ + 0.95);
    for (let y = Math.round(tp[1]); y <= Math.round(b[1]); y++) S.set(b[0], y, y === Math.round(tp[1]) + 1 && b[2] < 50 ? '#c8a050' : '#0e1018');
    if (b[2] < 36) for (let y = Math.round(tp[1]) + 1; y <= Math.round(b[1]); y++) S.set(b[0] + 1, y, '#232838');
  }
  for (let a = 53; a < 60; a += 0.9) {                 // the bike-share stand by the wall, a row of parked bikes
    const b = proj(a, -1.6, QZ), tp = proj(a, -1.6, QZ + 1.0), x = Math.round(b[0]);
    for (let y = Math.round(tp[1]); y <= Math.round(b[1]); y++) S.set(x, y, y === Math.round(tp[1]) ? '#5aa8c8' : '#161a28');
    S.set(x + 1, Math.round(b[1]), '#161a28'); S.set(x + 1, Math.round(tp[1]) + 1, '#2a6a8a');
  }
  { const b = proj(63, -9.5, QZ), tp = proj(63, -9.5, QZ + 3); for (let y = Math.round(tp[1]); y <= Math.round(b[1]); y++) S.set(b[0], y, '#12141e'); S.fill(Math.round(tp[0]), Math.round(tp[1]), 3, 3, '#e8c040'); }   // a bus stop's flag
  // the pub's striped awning over its east bay, where the busker stands
  quad3(S, [33.2, CSF, QZ + 3.05], [37.7, CSF, QZ + 3.05], [37.7, CSF + 2.3, QZ + 2.5], [33.2, CSF + 2.3, QZ + 2.5],
    (x, y) => ((x >> 1) & 1) ? [206, 196, 170] : [26, 92, 66]);
  for (let a = 33.2; a <= 37.7; a += 0.06) { const e = proj(a, CSF + 2.3, QZ + 2.5); S.set(e[0], e[1] + 1, (Math.round(e[0]) & 1) ? '#12382a' : '#8a8470'); }
  // the pub's hanging sign board (PUB) on its bracket, and the red vertical sign of the bar beyond Merchant's Arch
  {
    const tl = proj(PUB_SIGN.a, CSF + 0.35, QZ + 5.3), x = Math.round(tl[0]) - 14, y = Math.round(tl[1]);
    for (let j = y - 3; j <= y + 10; j++) for (let i = x - 2; i <= x + 16; i++) SL.set(i, j, [0, 0, 0], 0);
    S.fill(x - 2, y - 3, 18, 1, '#10121c'); S.set(x + 2, y - 2, '#10121c'); S.set(x + 11, y - 2, '#10121c');
    S.fill(x, y - 1, 15, 11, '#0c2a1e'); S.fill(x + 1, y, 13, 9, '#143c2c'); S.fill(x + 1, y, 13, 1, '#d8ac4c'); S.fill(x + 1, y + 8, 13, 1, '#8a6a2c');
    S.text('PUB', x + 2, y + 2, '#f0d48a');
    PUB_SIGN.x = x + 7; PUB_SIGN.y = y + 4;
  }
  BLADES.push(bladeSign(S, SL, BAR_SIGN.a, CSF + 0.95, QZ + 7.6, 'BAR', '#3a0c14', '#ff6a78', '#ff4a5c'));
  // a litter bin and a sandwich board outside the pub
  { const b = proj(40.4, -9.5, QZ), tp = proj(40.4, -9.5, QZ + 1.0); S.fill(Math.round(tp[0]) - 2, Math.round(tp[1]), 5, Math.round(b[1] - tp[1]) + 1, '#1c2030'); S.fill(Math.round(tp[0]) - 2, Math.round(tp[1]), 5, 1, '#48506a'); }
  { const b = proj(37.9, -9.3, QZ), tp = proj(37.9, -9.3, QZ + 0.95), x = Math.round(tp[0]) - 3, y = Math.round(tp[1]); S.fill(x, y, 6, Math.round(b[1]) - y + 1, '#20242e'); S.fill(x + 1, y + 1, 4, 4, '#e8e0c8'); S.set(x + 2, y + 2, '#a82c30'); S.set(x + 3, y + 3, '#a82c30'); S.set(x + 2, y + 4, '#a82c30'); }
  // on our quay wall's river face: mooring rings and their rust, an iron ladder, a drain's mouth (its trickle is drawn per frame)
  for (const a of [11.5, 21.5, 34]) {
    const q = proj(a, 0, 3.4), x = Math.round(q[0]), y = Math.round(q[1]), sz = Math.max(1, Math.round(F / q[2] * 0.2));
    for (let k = -sz; k <= sz; k++) { SW.set(x, y + k, '#0a0c14'); SW.set(x + 1, y + k, Math.abs(k) === sz ? '#0a0c14' : '#5a6078'); }
    for (let k = 1; k < 7 * sz; k++) if (hash(k, x, 3) < 0.75) SW.set(x + 1, y + sz + k, mixHex('#5a3424', '#2c3246', k / (7 * sz)));
  }
  {
    const b = proj(30, 0, 0.2), tp = proj(30, 0, CAPZ - 0.1), x = Math.round(b[0]);
    for (let y = Math.round(tp[1]); y <= Math.round(b[1]); y++) { SW.set(x, y, '#0c0e16'); if ((y & 3) === 0) SW.set(x - 1, y, '#3a4058'); }
  }
  SW.fill(OUTFALL.x - 1, OUTFALL.y - 3, 4, 5, '#06070c'); SW.fill(OUTFALL.x - 1, OUTFALL.y - 4, 4, 1, '#4a5270'); SW.set(OUTFALL.x + 3, OUTFALL.y - 2, '#4a5270');
}


/* ================= The rooftop hoarding on Bachelors Walk (the city's one billboard) =================
   A poster panel on a steel frame behind the parapets, turned to face the traffic that comes down the north quay
   (and us). Three small gooseneck lamps light it from above, no brighter than the night around it (BILL_NIGHT). Its
   own ad: a night of traditional music, TRAD TONIGHT. */
const STEEL = { d: '#0e121e', m: '#1a2032', h: '#39425c', w: '#7a6248' };
function renderHoarding() {
  const p = pix(W, H), L = BILL.x - 2, Rr = BILL.x + BILL.w + 1, T = BILL.y - 2, B = BILL.y + BILL.h + 1, FOOT = B + 30;
  const posts = [L + 4, L + 18, Rr - 18, Rr - 4];
  for (let k = 0; k + 1 < posts.length; k++) for (let j = 0; j < 3; j++) {                    // braces, alternating
    const a = posts[k] + 1, b = posts[k + 1] - 1, y0 = B + 3 + j * 9, y1 = y0 + 8;
    if ((k + j) & 1) p.line(a, y0, b, y1, STEEL.m); else p.line(a, y1, b, y0, STEEL.m);
  }
  for (const y of [B + 11, B + 20]) p.fill(posts[0], y, posts[3] - posts[0] + 1, 1, STEEL.d);
  for (const x of posts) { p.fill(x, B + 1, 1, FOOT - B, STEEL.d); p.set(x, B + 11, STEEL.h); p.set(x, B + 20, STEEL.h); }
  p.fill(L, B + 1, Rr - L + 1, 1, STEEL.h); p.fill(L, B + 2, Rr - L + 1, 1, STEEL.d);          // the catwalk under the panel
  p.fill(L, T, Rr - L + 1, B - T + 1, '#10241c');                                               // frame: dark green, a gilt bead
  p.fill(L, T, Rr - L + 1, 1, '#2c5a44'); p.fill(L, T, 1, B - T + 1, '#2c5a44');
  p.fill(L + 1, T + 1, Rr - L - 1, 1, '#b48c3e'); p.fill(L + 1, B - 1, Rr - L - 1, 1, '#8a6424');
  p.fill(L + 1, T + 1, 1, B - T - 1, '#8a6424'); p.fill(Rr - 1, T + 1, 1, B - T - 1, '#8a6424');
  p.fill(BILL.x, BILL.y, BILL.w, BILL.h, '#0d0f24');
  for (const lx of BILL_LAMPS) {                                                                // gooseneck lamps
    const y = BILL_LAMP_Y;
    p.fill(lx, y + 3, 1, T - y - 3, STEEL.m); p.set(lx, y + 3, STEEL.w);
    p.fill(lx - 1, y, 3, 1, STEEL.d); p.fill(lx - 2, y + 1, 5, 1, STEEL.d);
    p.fill(lx - 2, y + 2, 5, 1, '#d8b278'); p.fill(lx - 1, y + 2, 3, 1, '#f0d8a4');
  }
  return trim(p);
}
const PAPER = ['#857556', '#a8956e', '#c9b88e', '#e6d7ae', '#f6ecd0'];
// the poster at night (gradeAd): the paper sits two steps down its ramp, lit in three pools by small lamps, and the
// whole face is cooled a little towards the wet sky and streaked by the rain, so the Ha'penny Bridge's white iron
// stays the brightest thing on the river. The frame's gilt, the lenses and the halos are stepped down to match
const BILL_NIGHT = { level: 0.9, tint: '#19203a', mix: 0.08, wear: 0.35, seed: 73 };
const FIDDLE = { x: 2, y: 2, rows: [
  '.....ss.....', '.....sk.....', '.....nn.....', '.....nn.....', '.....nn.....', '...bbnnbb...', '..bBBnnBBb..', '..bBBnnBBb..',
  '...bBnnBb...', '..bBBnnBBb..', '.bBBBnnBBBb.', '.bBfBnnBfBb.', '.bBBBttBBBb.', '..bbBttBbb..', '....bbbb....'] };
const FIDDLE_KEY = { s: '#6a3a1c', k: '#2a1810', n: '#2a1c18', b: '#7a3c18', B: '#c46c2a', f: '#3a1c10', t: '#1c1210' };
function bigWord(p, s, x0, y0, col, hi, sh) {         // FONT3 at double size, one pixel between the letters
  [...s].forEach((ch, k) => {
    const g = FONT3[ch];
    g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') {
      p.fill(x0 + k * 7 + i * 2 + 1, y0 + j * 2 + 1, 2, 2, sh);
    } });
    g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') {
      p.fill(x0 + k * 7 + i * 2, y0 + j * 2, 2, 2, col); if (j === 0) p.fill(x0 + k * 7 + i * 2, y0, 2, 1, hi);
    } });
  });
}
function renderAd(dim) {                               // dim: the right-hand lamp is out
  const { w, h } = BILL, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    let cone = 0;
    BILL_LAMPS.forEach((lx, k) => {
      const dx = (i - (lx - BILL.x)) / (4 + j * 0.7);
      cone = Math.max(cone, (k === 2 && dim ? 0.25 : 1) * Math.exp(-dx * dx) * Math.max(0, 1 - j / 11));
    });
    let v = 0.25 + 0.125 * smooth(0.2, 0.32, cone) + 0.125 * smooth(0.55, 0.67, cone);          // tan paper, a pool under each lamp
    if (dim) v -= 0.22 * Math.exp(-(((i - (BILL_LAMPS[2] - BILL.x)) / 11) ** 2)) * (1 - j / (h + 12));
    p.set(i, j, pick(PAPER, v, i, j, 0.4));
  }
  p.fill(0, h - 2, w, 2, '#1c5a3c'); p.fill(0, h - 2, w, 1, '#2c7a54');                         // a green band along the foot
  FIDDLE.rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (FIDDLE_KEY[row[i]]) p.set(FIDDLE.x + i, FIDDLE.y + j, FIDDLE_KEY[row[i]]); });
  p.line(1, 15, 13, 5, '#e9dcc0'); p.line(1, 16, 13, 6, '#8a6a4a');                             // the bow, laid across it
  bigWord(p, 'TRAD', 17, 2, '#1c5a3c', '#2f8a5e', '#857556');
  p.text('TONIGHT', 17, 13, '#a8302a');
  return gradeAd(p.done(), BILL_NIGHT);
}

/* ================= The near lamp standard on our quay wall, and a ring buoy in its yellow box ================= */
const LAMP_NEAR = {};
function renderNearLamp(p) {
  const b = proj(LAMP_A, -0.22, CAPZ), X = Math.round(b[0]), Y1 = Math.round(b[1]), Y0 = Math.round(proj(LAMP_A, -0.22, CAPZ + 4.7)[1]);
  const I = '#0e101a', IH = '#394260', IW = '#8a6c4a';
  p.fill(X - 3, Y1 - 2, 7, 3, I); p.fill(X - 3, Y1 - 2, 7, 1, IH); p.fill(X - 2, Y1 - 6, 5, 4, I); p.set(X - 2, Y1 - 6, IW);   // base
  for (let y = Y0 + 14; y < Y1 - 6; y++) {                                                      // fluted shaft
    const wide = y > Y1 - 30;
    p.fill(X - 1, y, wide ? 3 : 2, 1, I); p.set(X - 1, y, y < Y0 + 34 ? IW : IH);
  }
  for (const y of [Y1 - 30, Y0 + 30]) { p.fill(X - 2, y, 5, 2, I); p.set(X - 2, y, IW); }
  p.fill(X - 5, Y0 + 15, 11, 1, I); p.set(X - 5, Y0 + 14, IW); p.set(X + 5, Y0 + 14, IW);        // the ladder bar
  for (let k = 0; k < 3; k++) { p.set(X - 2 - k, Y0 + 13 - k, I); p.set(X + 2 + k, Y0 + 13 - k, I); }
  for (let y = Y0; y <= Y0 + 12; y++) {                                                         // tapered lantern, lit panes
    const half = y < Y0 + 2 ? 4 : Math.round(4 - (y - Y0 - 2) * 2 / 10);
    p.fill(X - half, y, half * 2 + 1, 1, I);
    if (y >= Y0 + 2 && y <= Y0 + 11 && half > 1) { p.fill(X - half + 1, y, half * 2 - 1, 1, y < Y0 + 6 ? '#fff6dc' : y < Y0 + 9 ? '#ffe2a0' : '#ffc270'); p.set(X, y, '#c88a48'); }
  }
  for (const [dy, half] of [[-1, 5], [-2, 4], [-3, 3], [-4, 2], [-5, 1]]) { p.fill(X - half, Y0 + dy, half * 2 + 1, 1, I); p.set(X - half, Y0 + dy, IW); }
  p.fill(X, Y0 - 9, 1, 4, I); p.set(X, Y0 - 10, IW);
  LAMP_NEAR.x = X; LAMP_NEAR.y = Y0 + 6; LAMP_NEAR.base = Y1;
  // the ring buoy by the bridge's end: a yellow box on the parapet, the red and white ring showing
  const q = proj(37, -0.25, CAPZ), bx = Math.round(q[0]), by = Math.round(q[1]);
  p.fill(bx, by - 9, 1, 9, I); p.fill(bx - 3, by - 16, 7, 8, '#d8b020'); p.fill(bx - 3, by - 16, 7, 1, '#f2d24a');
  p.fill(bx - 2, by - 15, 5, 5, '#e8e4dc'); p.fill(bx - 1, by - 14, 3, 3, '#d8b020'); p.set(bx - 2, by - 15, '#d8342c'); p.set(bx + 2, by - 15, '#d8342c'); p.set(bx - 2, by - 11, '#d8342c'); p.set(bx + 2, by - 11, '#d8342c');
}

/* ================= Traffic: double-deckers and a taxi on the north quay (in perspective), far buses, the DART ================= */
// Bachelors Walk is one-way to the east: the buses drive away from us, their river side and their backs to us.
const LANE_C = CNF - 6.3;                             // the river-side face of the bus lane's vehicles
const LIVERY = [                                      // yellow with a blue-green sweep; yellow and blue (the older coat)
  { body: '#e6b81c', band: '#0f8a72', rear: '#0f8a72', roof: '#c79c14' },
  { body: '#e6b81c', band: '#2c4cae', rear: '#e6b81c', roof: '#2c4cae' },
];
const BUS_V = { len: 11, h: 4.4, w: 2.5, bays: 7 }, TAXI_V = { len: 4.6, h: 1.5, w: 1.8, bays: 2 };
const hazeHex = (h, d) => toHex(mixc(C(h), HAZE, clamp((1 - Math.exp(-d / 200)) * 0.8, 0, 1)));
function drawQuayVehicle(a0, V, L, t, braking, blink) {
  const pr = proj(a0, LANE_C, QZ), d0 = pr[2];
  if (d0 < 20) return;
  const xr = Math.round(pr[0]), xl = Math.round(proj(a0, LANE_C + V.w, QZ)[0]), xf = Math.round(proj(a0 + V.len, LANE_C, QZ)[0]);
  const Y = (z, d) => Math.round(HZ - F * (QZ + z - EYE) / d);
  const body = hazeHex(L.body, d0), band = hazeHex(L.band, d0), rear = hazeHex(L.rear, d0), roof = hazeHex(L.roof, d0);
  const lit = hazeHex('#ffe2a4', d0 * 0.5), dark = hazeHex('#141824', d0), bus = V === BUS_V;
  // the back
  const yt = Y(V.h, d0), yb = Y(0.25, d0), rw = Math.max(1, xr - xl);
  R(xl, yt, rw, yb - yt, rear); R(xl, yt, rw, 1, roof);
  if (bus) {
    R(xl + 1, Y(3.85, d0), Math.max(1, rw - 2), Math.max(1, Y(2.95, d0) - Y(3.85, d0)), lit);          // upper deck's back window
    if (rw > 5) R(xl + 1, Y(2.3, d0), Math.max(1, rw - 2), Math.max(1, Y(1.6, d0) - Y(2.3, d0)), hazeHex('#2a3048', d0));
    if (rw > 4) R(xl + (rw >> 1) - 1, yt + 1, 3, 1, '#ffb040');                                        // route number, in amber
  } else { R(xl + 1, Y(1.25, d0), Math.max(1, rw - 2), 1, hazeHex('#56607c', d0)); R(xl + (rw >> 1), yt - 1, 1, 1, '#ffd060'); }
  const ty = Y(0.9, d0), tl = braking ? '#ff5a48' : '#d8302c';
  dot(xl, ty, tl); dot(xr - 1, ty, tl);
  if (blink && frac(t / 0.8) < 0.5) dot(xl, ty - 1, '#ffb040');
  halo(HL.tail, xl, ty, braking ? 1 : 0.6); halo(HL.tail, xr - 1, ty, braking ? 1 : 0.6);
  // the side that faces the river, column by column
  for (let x = xr; x <= xf; x++) {
    const a = aAt(x + 0.5, LANE_C), u = (a - a0) / V.len;
    if (u < 0 || u > 1) continue;
    const d = a * CT + (LANE_C - CAMC) * ST, y0 = Y(V.h, d), y1 = Y(0.25, d);
    R(x, y0, 1, y1 - y0, body);
    const pillar = frac(u * V.bays + 0.1) < 0.2 || u < 0.03 || u > 0.97;
    if (bus) {
      R(x, y0, 1, Math.max(1, Y(3.95, d) - y0), roof);
      R(x, Y(2.75, d), 1, Math.max(1, Y(2.3, d) - Y(2.75, d)), band);
      if (!pillar) { R(x, Y(3.85, d), 1, Math.max(1, Y(2.9, d) - Y(3.85, d)), lit); R(x, Y(2.25, d), 1, Math.max(1, Y(1.3, d) - Y(2.25, d)), lit); }
      if (u > 0.66) R(x, Y(1.25, d), 1, Math.max(1, y1 - Y(1.25, d)), band);                           // the sweep over the front wheel
    } else if (!pillar && u > 0.2 && u < 0.8) R(x, Y(1.4, d), 1, Math.max(1, Y(0.95, d) - Y(1.4, d)), hazeHex('#8a96b4', d));
    if ((u > 0.14 && u < 0.24) || (u > 0.74 && u < 0.84)) R(x, Y(0.85, d), 1, Math.max(1, y1 - Y(0.85, d) + 1), dark);
  }
  halo(HL.win, (xr + xf) >> 1, Y(V.h * 0.55, d0), bus ? 0.55 : 0.2);
  return { x: (xr + xf) >> 1, d: d0 };
}
/** where a north-quay bus is: it comes in on the left, stops at the shelter, pulls out and runs on to O'Connell Street */
function busAt(b, t) {
  const u = (t - b.t0) / (b.t1 - b.t0);
  if (u < 0 || u >= 1) return null;
  const a = u < 0.3 ? lerp(38, 86, 1 - Math.pow(1 - u / 0.3, 2)) : u < 0.5 ? 86 : lerp(86, 204, Math.pow((u - 0.5) / 0.5, 1.7));
  return { a, braking: u > 0.2 && u < 0.5, blink: u > 0.22 && u < 0.52, fade: 1 - smooth(0.93, 1, u) };
}
const OBUS_W = 17, OBUS_H = 7;
function renderFarBus(L, flip) {                      // a double-decker seen side-on, far off on O'Connell Bridge
  const p = pix(OBUS_W, OBUS_H), X = i => flip ? OBUS_W - 1 - i : i;
  for (let j = 0; j < OBUS_H; j++) for (let i = 0; i < OBUS_W; i++) {
    let col = hazeHex(L.body, 200);
    if (j === 0) col = hazeHex(L.roof, 200); else if (j === 3) col = hazeHex(L.band, 200);
    else if ((j === 1 || j === 2 || j === 4) && i % 3 !== 0 && i > 0 && i < OBUS_W - 1) col = '#f0d49a';
    if (j === 6) col = (i > 1 && i < 5) || (i > 11 && i < 15) ? '#10121c' : hazeHex(L.body, 200);
    if (i > 12 && j > 3 && j < 6) col = hazeHex(L.band, 200);
    p.set(X(i), j, col);
  }
  p.set(X(OBUS_W - 1), 5, '#fff4d0'); p.set(X(0), 5, '#e03a30');
  return p.done();
}
function renderDart() {                               // the DART: a green electric train, its windows lit
  const p = pix(40, 3);
  for (let i = 0; i < 40; i++) { p.set(i, 0, '#49615a'); p.set(i, 1, i % 10 === 9 ? '#2f4a42' : i % 2 ? '#f0dca8' : '#6a9a7c'); p.set(i, 2, '#3e7a5c'); }
  return p.done();
}

/* ================= A river boat, seen from astern as it runs down the Liffey and under the arch ================= */
const BOAT_C0 = 15;                                   // its line down the river (metres from the south wall)
function boatAt(t) {
  const u = (t - BOAT.t0) / (BOAT.t1 - BOAT.t0);
  if (u < 0 || u >= 1) return null;
  const a = lerp(13, 198, u), p = proj(a, BOAT_C0 + 2 * Math.sin(u * 5), 0);
  return { a, x: p[0], y: p[1], sc: F / p[2], fade: Math.min(1, u * 14) * (1 - smooth(0.86, 1, u)) };
}
function drawBoat(b, t) {
  const s = b.sc, X = Math.round(b.x), Yw = Math.round(b.y), hw = Math.max(1, Math.round(1.35 * s)), hh = Math.max(1, Math.round(0.95 * s));
  const cw = Math.max(1, Math.round(0.95 * s)), ch = Math.max(1, Math.round(1.25 * s)), roll = Math.round(Math.sin(TAU * 60 * t / LOOP) * 0.5 * Math.min(1, s / 6));
  ctx.globalAlpha = b.fade;
  for (let k = 0; k < 14; k++) {                                                                // the wake, spreading towards us
    const v = (k + frac(t * 1.5)) / 14, wy = Yw + v * 2.6 * s, wx = (0.9 + v * 2.4) * s;
    if (hash(k, step(t, 960), 31) < 0.7 - v * 0.4) { dot(X - wx, wy, k < 5 ? '#c4cce0' : '#7884a4'); dot(X + wx, wy, k < 5 ? '#c4cce0' : '#7884a4'); }
  }
  R(X - hw + roll, Yw - hh, hw * 2, hh, '#18203a'); R(X - hw + roll, Yw - hh, hw * 2, 1, '#6a7694');   // hull (stern) and its gunwale
  if (hh > 3) R(X - hw + 1 + roll, Yw - 1, hw * 2 - 2, 1, '#7a2a2c');                                  // boot-top
  R(X - cw + roll, Yw - hh - ch, cw * 2, ch, '#aab2c8'); R(X - cw + roll, Yw - hh - ch, cw * 2, 1, '#dfe4f0');   // wheelhouse
  if (cw > 1) R(X - cw + 1 + roll, Yw - hh - ch + Math.max(1, Math.round(ch * 0.25)), cw * 2 - 2, Math.max(1, Math.round(ch * 0.4)), '#ffd890');
  dot(X + roll, Yw - hh - ch - Math.max(1, Math.round(0.5 * s)), '#ffffff');                           // stern light on its staff
  if (s > 3) { dot(X - hw + roll, Yw - hh - 1, '#e8443a'); dot(X + hw - 1 + roll, Yw - hh - 1, '#4ad08a'); }
  for (let j = 0; j < Math.round(hh + ch); j++) {                                                      // its mirror image, broken by the wash
    ctx.globalAlpha = b.fade * 0.32 * (1 - j / (hh + ch + 1));
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.8) * (0.5 + j * 0.1));
    R(X - (j < hh ? hw : cw) + off, Yw + 1 + j, (j < hh ? hw : cw) * 2, 1, j < hh ? '#0a0e1c' : '#8088a0');
  }
  ctx.globalAlpha = b.fade;
  halo(HL.boat, X, Yw - hh - (ch >> 1), 0.7 * Math.min(1, s / 5));
  ctx.globalAlpha = 1;
  streak(ctx, t, X, Math.max(1, Math.round(s * 0.4)), '#ffd890', 0.3 * b.fade, 5, Yw + 2, Math.min(H, Yw + 2 + Math.round(4 * s)));
}

/* ================= Birds: gulls on the lamps and the quay wall, a cormorant fishing ================= */
const GULL_KEY = { w: '#e4e8f2', g: '#9aa4be', k: '#2a3048', y: '#f0b040' };
const GULL_SIT = ['...ww.', '..wwwy', 'ggwww.', 'gggw..', '.kgg..', '..y...'];
const GULL_FLY = [['w.....w', '.ww.ww.', '...g...'], ['.......', 'wwwgwww', '...g...'], ['.......', '.wwgww.', 'w.....w']];
const CORM_KEY = { k: '#0e121c', d: '#283040', y: '#c8a050' };
const CORM = ['...kk', '..kky', '..k..', 'kkkk.', 'dkkd.'];
// where the gulls sit: [a, c, z] on the bridge's lamp arches, the quay walls, a lamp standard
const PERCH = [[AB, ARCH_C[1], deckZ(ARCH_C[1]) + RAILH + ARCH_H], [AB - BW, 13, deckZ(13) + RAILH], [AB - BW, 30.5, deckZ(30.5) + RAILH],
  [33, -0.2, CAPZ], [41, -0.2, CAPZ], [62, RW + 0.2, CAPZ], [AB, ARCH_C[2], deckZ(ARCH_C[2]) + RAILH + ARCH_H]];
// the herring gull that stands on our coping near us, looking out over the river (facing left, feet on the bottom row):
// it stands, now and then peers down at the water or turns round, and it cries when the bell sends the others up
const NEAR_GULL_KEY = { h: '#e8dcc6', w: '#c2c8d8', s: '#8a92aa', g: '#7c86a2', G: '#5a6482', K: '#151924', m: '#b4bacc',
  y: '#d8aa3a', r: '#c03c2c', e: '#121420', p: '#a87e78', o: '#4a1a1a' };
const NEAR_GULL_BODY = ['....wwwwhgg........', '...wwwwhggggggg....', '...wwwwgggggggggg..', '...wwwwwGGGGGGKKKK.', '....wwwwwwwsGKKmKK.',
  '.....ssssssss..KK..', '........p.p........', '.......pp.pp.......'];
const NEAR_GULL_HEADS = [
  ['....hhh............', '...hwwwh...........', '.yywewww...........', 'yrywwwww...........'],   // looking out over the river
  ['...................', '....hhh............', '...hwwwh...........', '.yywewww...........', 'yrywwwwwhgg........'],   // peering down at the water
  ['....hhh............', '.yyhwwwh...........', '..owewww...........', 'yrywwwww...........'],   // crying, the bill open
];
const NEAR_GULL_CRIES = [[100, 101.8], [134.4, 136.2]];
const NEAR_GULL = (() => { const p = proj(11, -0.2, CAPZ); return { x: Math.round(p[0]), y: Math.round(p[1]) }; })();
const GULLS = PERCH.map((q, i) => {
  const r = rng(700 + i), p = proj(q[0], q[1], q[2]);
  return { i, x: Math.round(p[0]), y: Math.round(p[1]), big: p[2] < 42, laps: 1 + (r() < 0.4 ? 1 : 0), phi: r() * TAU, rx: 26 + r() * 40, ry: 9 + r() * 14,
    bx: (r() < 0.5 ? -1 : 1) * (20 + r() * 50), by: 22 + r() * 30, del: r() * 0.08, flip: r() < 0.5 };
});


/* ================= People =================
   One function draws everyone (after the Trevi's person()): a head, a body, legs, a colour each, dimmed for the night
   and lit along the shoulders by the lamps. Everyone has a fixed rank and is drawn while rank < crowd; rank 0 is the
   cast the scene's story needs (the man at the quay wall, the busker, the two friends at the pub door). Crowd 0
   draws nobody: the guitar, the bikes' riders and the cup go with them; buses, the boat and the birds stay. */
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const NIGHT = '#10142a', GLOWC = '#ffd8a0';
const COATS = ['#3a4a6a', '#6a2e34', '#2e5a4a', '#8a7a5a', '#4a4a58', '#b8423a', '#2a2e3c', '#c8a23c', '#5a3a6a', '#d8d0c0', '#356a8a', '#7a4a2a'];
const BOTS = ['#28304a', '#1e1e28', '#4a4038', '#3a3a50', '#5a5a68', '#2c3a34'];
const BROLLIES = ['#1a1c28', '#8e1e2a', '#1e4a7a', '#2a6a4a', '#c8a030', '#5a2a6a', '#c8c8d4', '#b8522a'];
/** a look: the proportions for a height of h px, and its colours dimmed for the night (dk) */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.28, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.35), bot: m(bot), shoe: m('#1a161c'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, bag: o.bag ? m(o.bag) : null, hood: !!o.hood };
}
/**
 * One person, feet on row fy, centred on column x. Returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir: 1 right, -1 left); o.step: stride frame; o.lean: head px aside
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up with a phone, 3 out, 4 bent up, 5 hand to the face, 6 hand to the mouth (profile)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = P.ll, ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  const ly = fy - legs + 1;
  if (side) {
    if (o.step) {
      R(x - 2, ly, 4, 2, legC);
      R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
      R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
    } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
  } else {
    const lw = bw >= 7 ? 3 : 2, sl = o.step === 1 ? 1 : 0, sr = o.step === 2 ? 1 : 0;   // walking towards or away from us: one foot lifts
    R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2 - sl, legC); R(tx + bw - lw, ly + 2, lw, legs - 2 - sr, legC);
    R(tx, fy - sl, lw, 1, P.shoe); R(tx + bw - lw, fy - sr, lw, 1, P.shoe);
    if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
  }
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                          // the lamps' light on the shoulders
  if (P.bag) { if (view === 0) R(tx + 1, ty + 1, bw - 2, th - 2, P.bag); else if (side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 1, 2, th - 2, P.bag); }
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 2 ? P.topD : P.skin); R(x + dir * 4 - (dir < 0 ? 1 : 0), ty - 3, 2, 2, '#cfe6ff'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2 || st === 4) {
        R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
        if (st === 2) R(sg < 0 ? ax - 1 : ax, hy - 1, 2, 2, '#cfe6ff');
      } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0 || P.hood) {
    R(hx, hy, hw, hh, P.hood ? P.top : P.hair); R(hx, hy, hw, 1, P.hood ? P.topL : P.hairL);
    if (P.hood && view === 1) R(hx + 1, hy + 1, hw - 2, hh - 2, P.skin);
    else if (P.hood && side) R(dir > 0 ? hx + hw - 1 : hx, hy + 1, 1, hh - 2, P.skin);
    else if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else if (!P.hood) R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); else R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}
/** an umbrella over a head whose top is at row hy */
const BROLLY_ROWS = { 3: [1, 3, 3], 4: [2, 3, 4], 5: [2, 4, 5, 5], 6: [2, 4, 5, 6, 6], 7: [3, 5, 6, 7, 7] };
function brolly(x, hy, h, col) {
  const hw = h >= 24 ? 7 : h >= 20 ? 6 : h >= 16 ? 5 : h >= 12 ? 4 : 3, rows = BROLLY_ROWS[hw], y0 = hy + 1 - rows.length;
  rows.forEach((w, j) => {
    R(x - w, y0 + j, w * 2 + 1, 1, j === rows.length - 1 ? mixHex(col, '#000000', 0.35) : col);
    if (j < rows.length - 1) R(x - w, y0 + j, Math.max(1, w - 1), 1, mixHex(col, GLOWC, 0.3));
  });
  R(x, y0 - 1, 1, 1, '#0c0d14');
}
/** someone's looks at every size they are drawn at (they grow as they come down the quay) */
function kit(seed, o = {}) {
  const r = rng(seed), sk = Math.floor(r() * 4), hr = Math.floor(r() * 6), top = o.top || COATS[Math.floor(r() * COATS.length)], bot = BOTS[Math.floor(r() * BOTS.length)];
  const long = r() < 0.4 ? 2 : 0, hood = o.hood ?? r() < 0.2, bag = r() < 0.25 ? '#5a3a2a' : null, L = [];
  const k = h => L[h] || (L[h] = look(h, sk, hr, top, bot, { long: h > 12 ? long : 0, hood, bag, dk: o.dk ?? 0.3 }));
  k.brolly = o.brolly === false || hood ? null : BROLLIES[Math.floor(r() * BROLLIES.length)];
  return k;
}
/** where feet at (a, c) on the south quay stand on screen, and how tall the person is there */
function onQuay(a, c, z = QZ) {
  const d = a * CT + (c - CAMC) * ST;
  return { x: CXP + F * (a * ST - (c - CAMC) * CT) / d, y: HZ - F * (z - EYE) / d, h: Math.max(4, Math.round(F * PH / d)), d };
}
const walkF = t => Math.floor(t * 3.2) & 1;            // stride frame (3.2 a second: a whole number per loop)
let DL = [];                                           // the frame's people on our quay, drawn back to front
const put = (y, f) => DL.push({ y, f });

/* ----- the cast that stays ----- */
const LEANER = { a: 27.5, c: -0.55, k: kit(11, { top: '#4a5a44', brolly: false, hood: false }) };
const BUSKER = { a: 33.7, c: -10.8, k: kit(12, { top: '#7a4a2a', brolly: false, hood: false, dk: 0.16 }) };
const DOOR = [{ a: 38.9, c: -10.9, k: kit(13, { top: '#b8423a', brolly: false, hood: false, dk: 0.14 }) }, { a: 39.75, c: -10.3, k: kit(14, { top: '#356a8a', brolly: false, hood: false, dk: 0.14 }) }];
const LISTENER = { a: 33, c: -2.6, k: kit(15, { top: '#c8a23c', hood: true }), rank: 0.3 };
const PHONE = { a: 30, c: -11.3, k: kit(16, { top: '#5a3a6a', brolly: false, hood: false, dk: 0.16 }), rank: 0.45 };
const LAMP_PAIR = [{ a: 25.4, c: -2.1, k: kit(17, { top: '#d8d0c0', brolly: false, hood: false }) }, { a: 25.2, c: -2.9, k: kit(18, { top: '#2e5a4a', brolly: false, hood: false }) }];
const LAMP_PAIR_RANK = 0.85;
const WINDOW_PAIR = [{ a: 31.3, c: -11.05, k: kit(19, { top: '#356a8a', brolly: false, hood: false, dk: 0.16 }) }, { a: 32.1, c: -10.7, k: kit(20, { top: '#c8a23c', brolly: false, hood: true, dk: 0.16 }) }];
const WINDOW_PAIR_RANK = 0.72;
const ARCH_MAN = { a: 44.3, c: -11.4, k: kit(21, { top: '#d8d0c0', brolly: false, hood: false, dk: 0.14 }), rank: 0.6 };
const NOTES = [[0, 0.0], [1, 0.37], [2, 0.71]];
function drawCast(t, crowd) {
  {                                                    // the man at the quay wall, under the lamp: now and then he lifts his cup
    const s = onQuay(LEANER.a, LEANER.c), P = LEANER.k(s.h), u = frac(t / CUP_T), sip = u > 0.1 && u < 0.22;
    const turn = hash(3, step(t, 40), 21) < 0.2 && !sip;
    put(s.y, () => {
      const hy = person(s.x, s.y, P, turn ? { view: 1 } : { view: 2, dir: -1, aR: sip ? 6 : 3 });
      if (!turn) R(Math.round(s.x) - (sip ? 2 : 5), hy + (sip ? P.hh - 1 : P.hh + 3), 2, 2, '#e8e2d4');   // his paper cup
    });
  }
  {                                                    // the busker under the pub's awning: a guitar, a strumming hand, a few notes
    const s = onQuay(BUSKER.a, BUSKER.c), P = BUSKER.k(s.h), X = Math.round(s.x), strum = Math.floor(t * 3.2) % 4;
    put(s.y, () => {
      const hy = person(X, s.y, P, { view: 1, lean: hash(4, step(t, 60), 22) < 0.3 ? -1 : 0 }), gy = hy + P.hh + 3;
      R(X - 2, gy, 5, 4, '#b06a2a'); R(X - 1, gy + 1, 3, 2, '#e09a4a'); R(X, gy + 1, 1, 1, '#3a1c10');             // the guitar's body
      for (let k = 0; k < 6; k++) R(X + 3 + k, gy - k, 1, 1, k < 5 ? '#4a2c1a' : '#d8c8a0');                      // its neck, up to his left hand
      R(X - 1 + (strum & 1), gy + (strum > 1 ? 1 : 2), 1, 1, P.skin);
      for (const [k, ph] of NOTES) {                   // notes drift up from the guitar
        const v = frac(t / 6 + ph), nx = X + 6 + Math.round(Math.sin(v * 7 + k) * 3 + v * 6), ny = Math.round(gy - 4 - v * 16);
        ctx.globalAlpha = Math.min(1, v * 5) * (1 - v) * 0.9;
        R(nx, ny, 2, 2, '#ffe9b0'); R(nx + 1, ny - 3, 1, 3, '#ffe9b0'); if (k & 1) R(nx + 2, ny - 3, 1, 1, '#ffe9b0');
        ctx.globalAlpha = 1;
      }
    });
  }
  DOOR.forEach((q, i) => {                             // two friends talking in the pub's doorway, in its light
    const s = onQuay(q.a, q.c), P = q.k(s.h), talk = hash(5 + i, step(t + i * 1.7, 96), 23) < 0.4;
    put(s.y, () => {
      const hy = person(s.x, s.y, P, { view: 2, dir: i ? 1 : -1, aR: talk ? 3 : i && frac(t / 16) < 0.2 ? 6 : 0, lean: hash(7 + i, step(t, 48), 24) < 0.25 ? (i ? 1 : -1) : 0 });
      if (i) R(Math.round(s.x) + 2, hy + P.hh + 1, 1, 2, '#e8a030');                                           // his glass
    });
  });
  if (LISTENER.rank < crowd) {                         // someone has stopped across the road to listen
    const s = onQuay(LISTENER.a, LISTENER.c), P = LISTENER.k(s.h), sway = Math.round(0.6 * wave(t, 40, 0.3));
    put(s.y, () => person(s.x + sway, s.y, P, { view: 0, aR: frac(t / 30) > 0.9 ? 4 : 0 }));
  }
  if (PHONE.rank < crowd) {                            // out of the rain under the awning, on the phone
    const s = onQuay(PHONE.a, PHONE.c), P = PHONE.k(s.h), up = frac(t / 20 + 0.3) < 0.75;
    put(s.y, () => { person(s.x, s.y, P, { view: 2, dir: -1, aR: up ? 2 : 0 }); });
  }
  if (WINDOW_PAIR_RANK < crowd) WINDOW_PAIR.forEach((q, i) => {   // two more have stopped for the music, by the pub's window
    const s = onQuay(q.a, q.c), P = q.k(s.h);
    put(s.y, () => person(s.x, s.y, P, i ? { view: 2, dir: -1, aR: frac(t / 30 + 0.4) < 0.12 ? 4 : 0 } : { view: 0, lean: hash(8, step(t, 40), 25) < 0.3 ? 1 : 0 }));
  });
  if (ARCH_MAN.rank < crowd) {                         // someone waits at Merchant's Arch, looking at a phone
    const s = onQuay(ARCH_MAN.a, ARCH_MAN.c), P = ARCH_MAN.k(s.h);
    put(s.y, () => person(s.x, s.y, P, { view: 2, dir: -1, aR: frac(t / 24 + 0.6) < 0.8 ? 2 : 0 }));
  }
  if (LAMP_PAIR_RANK < crowd) LAMP_PAIR.forEach((q, i) => {   // two under one umbrella, looking down the river
    const s = onQuay(q.a, q.c), P = q.k(s.h), pt = i === 0 && frac(t / 40 + 0.2) < 0.08;
    put(s.y + i, () => {
      const hy = person(s.x, s.y, P, { view: 0, aL: pt ? 3 : 0, lean: i ? -1 : 1 });
      if (i) brolly(Math.round(s.x) - 5, hy, s.h + 4, '#8e1e2a');
    });
  });
}

/* ----- people walking the quay's far footpath (by the house fronts): they come down it towards us, or go up it ----- */
const WALKERS = [];
{
  const r = rng(52);
  for (let i = 0; i < 6; i++) {
    const pair = i === 1 || i === 4;
    WALKERS.push({ rank: i === 0 ? 0.1 : rankOf(i, 0.07), dir: i & 1 ? 1 : -1, ph: (i + r() * 0.5) / 6, c: -10.2 - r() * 0.9, k: kit(60 + i), k2: pair ? kit(80 + i) : null });
  }
}
const DOGGER = { rank: 0.38, k: kit(95, { top: '#b8423a', hood: true }) };
const PATRON = { rank: 0.25, ks: [kit(101, { brolly: false }), kit(102, { brolly: false }), kit(103, { brolly: false }), kit(104, { brolly: false })] };
function stroller(s, P, k, toward, t, id, alpha, bro) {
  put(s.y, () => {
    ctx.globalAlpha = alpha;
    const hy = person(s.x, s.y, P, { view: toward ? 1 : 0, step: ((walkF(t) + id) & 1) + 1 });
    if (bro) brolly(Math.round(s.x) + (id & 1 ? 1 : -1), hy, s.h, bro);
    ctx.globalAlpha = 1;
  });
}
function drawWalkers(t, crowd) {
  WALKERS.forEach((w, i) => {
    if (w.rank >= crowd) return;
    const u = frac(t * 2 / LOOP + w.ph), a = w.dir > 0 ? lerp(25.4, 150, u) : lerp(150, 25.4, u), s = onQuay(a, w.c);
    if (s.x > W + 8) return;
    const al = clamp((150 - a) / 24, 0, 1);
    stroller(s, w.k(s.h), w.k, w.dir < 0, t, i, al, w.k.brolly);
    if (w.k2) { const s2 = onQuay(a + 0.5, w.c + 0.85); stroller(s2, w.k2(s2.h), w.k2, w.dir < 0, t, i + 1, al, w.k.brolly ? null : w.k2.brolly); }
  });
  if (DOGGER.rank < crowd) {                           // a dog walked down the wall side to the lamp and back up
    const u = frac(t / LOOP + 0.62), out = u < 0.42, a = out ? lerp(140, 30, u / 0.42) : u < 0.58 ? 30 : lerp(30, 140, (u - 0.58) / 0.42);
    const s = onQuay(a, -2.3), P = DOGGER.k(s.h), stop = u >= 0.42 && u < 0.58, al = clamp((140 - a) / 24, 0, 1);
    put(s.y, () => {
      ctx.globalAlpha = al;
      person(s.x, s.y, P, { view: u < 0.5 ? 1 : 0, step: stop ? 0 : (walkF(t) & 1) + 1 });
      const sc = Math.max(1, Math.round(s.h / 9)), dx = Math.round(s.x) - 2 * sc - 3, wag = stop ? Math.floor(t * 4) & 1 : walkF(t);
      R(dx, Math.round(s.y) - 2 * sc + 1, 3 * sc, sc, '#c8b48a'); R(dx - sc + (u < 0.5 ? 0 : 3 * sc), Math.round(s.y) - 3 * sc + 1, sc, sc + (stop ? 1 : 0), '#c8b48a');   // the dog
      R(dx, Math.round(s.y) - sc + 1, 1, sc, '#8a7a5a'); R(dx + 3 * sc - 1, Math.round(s.y) - sc + 1 - wag, 1, sc, '#8a7a5a');
      ctx.globalAlpha = 1;
    });
  }
  if (PATRON.rank < crowd) {                           // every minute the pub door opens and someone leaves, up the quay
    const n = Math.floor(t / DOOR_T), v = t - n * DOOR_T;
    if (v > 1.2 && v < 50) {
      const a = v < 3.4 ? 39.3 : lerp(39.3, 100, (v - 3.4) / 46.6), c = v < 3.4 ? lerp(-11.9, -10.6, (v - 1.2) / 2.2) : -10.6;
      const s = onQuay(a, c), k = PATRON.ks[n % 4];
      stroller(s, k(s.h), k, false, t, 3, Math.min(1, (v - 1.2) / 1.2) * clamp((100 - a) / 20, 0, 1), null);
    }
  }
}

/* ----- riders on Wellington Quay: a cyclist coming down it, a courier going up it (the quay is one-way to the west) ----- */
function drawRiders(t, crowd) {
  if (0.15 < crowd && t >= BIKE.t0 && t < BIKE.t1) {
    const u = (t - BIKE.t0) / (BIKE.t1 - BIKE.t0), s = onQuay(lerp(150, 14.5, Math.pow(u, 0.9)), -6.3), X = Math.round(s.x), Y = Math.round(s.y), h = s.h;
    const wh = Math.max(2, Math.round(h * 0.4)), P = RIDER.k(Math.max(6, Math.round(h * 0.72))), ped = walkF(t * 1.5);
    put(s.y, () => {
      ctx.globalAlpha = Math.min(1, u * 8);
      R(X, Y - wh + 1, h > 16 ? 2 : 1, wh, '#0c0e16');                                               // front wheel, end on
      const hy = person(X, Y - Math.round(wh * 0.55), P, { view: 1, step: ped + 1, aL: 3, aR: 3 });
      R(X - (P.tw >> 1) - 2, hy + P.hh + 2, P.tw + 4, 1, '#2a2e3c');                                  // handlebars
      R(X, Y - wh - 1, 1, 1, '#ffffff'); ctx.globalAlpha = 1;
      halo(HL.bikeLamp, X, Y - wh - 1, Math.min(1, u * 8)); drawPool(HL.poolBike, X, Y + 2 + Math.round(h * 0.2), 0.5 * Math.min(1, h / 14));
    });
  }
  if (0.4 < crowd && t >= COURIER.t0 && t < COURIER.t1) {
    const u = (t - COURIER.t0) / (COURIER.t1 - COURIER.t0), s = onQuay(lerp(14.5, 150, Math.pow(u, 1.25)), -4.6), X = Math.round(s.x), Y = Math.round(s.y), h = s.h;
    const wh = Math.max(2, Math.round(h * 0.4)), P = COURIER_K(Math.max(6, Math.round(h * 0.72))), ped = walkF(t * 1.5);
    put(s.y, () => {
      ctx.globalAlpha = 1 - smooth(0.8, 1, u);
      R(X, Y - wh + 1, h > 16 ? 2 : 1, wh, '#0c0e16');
      const hy = person(X, Y - Math.round(wh * 0.55), P, { view: 0, step: ped + 1 });
      const bs = Math.max(2, Math.round(h * 0.3));                                                    // the square bag on his back
      R(X - (bs >> 1), hy + P.hh, bs, bs, '#19a39a'); R(X - (bs >> 1), hy + P.hh, bs, 1, '#5fd6cc');
      if (frac(t * 2) < 0.6) R(X, Y - wh, 1, 1, '#ff4030');
      ctx.globalAlpha = 1;
      if (frac(t * 2) < 0.6) halo(HL.tail, X, Y - wh, 0.8 * (1 - smooth(0.8, 1, u)));
    });
  }
}
const RIDER = { k: kit(120, { top: '#c8a23c', brolly: false, hood: true }) }, COURIER_K = kit(121, { top: '#2a2e3c', brolly: false, hood: true });

/* ----- people over the Ha'penny Bridge, in ones and twos under umbrellas: each crosses once every 120 s ----- */
// their way: along the north quay's footpath, over the bridge, across Wellington Quay and in through Merchant's Arch
const CROSS = [[0, 60, RW + 1.4], [0.16, AB, RW + 1.2], [0.2, AB, RW], [0.84, AB, 0], [0.9, AB, -3.2], [1, 45.6, -11.6]];
const CROSSERS = [], CROSS_RANKS = [0, 0.3, 0.55, 0.8, 0.2, 0.65, 0.9, 0.45];   // one of them is there at every level above 0; two of the eight are pairs
{
  const r = rng(53);
  for (let i = 0; i < 8; i++) CROSSERS.push({ rank: CROSS_RANKS[i], south: i % 3 !== 1, ph: (i * 0.618 + r() * 0.1) % 1, k: kit(140 + i, { dk: 0.2 }), k2: i === 4 || i === 5 ? kit(160 + i, { dk: 0.2 }) : null, off: (r() - 0.5) * 1.6 });
}
const CROSS_D = 62 / 120;                              // the share of its 120 s a crosser is on the way
function crossAt(cr, t, lag = 0) {
  const u0 = frac(t * 2 / LOOP + cr.ph) / CROSS_D - lag;
  if (u0 < 0 || u0 >= 1) return null;
  const u = cr.south ? u0 : 1 - u0;
  let k = 1; while (k < CROSS.length - 1 && CROSS[k][0] < u) k++;
  const A = CROSS[k - 1], B = CROSS[k], v = (u - A[0]) / (B[0] - A[0]), a = lerp(A[1], B[1], v) + (k === 3 ? cr.off * 0.5 : 0), c = lerp(A[2], B[2], v);
  const z = c >= 0 && c <= RW ? deckZ(c) : QZ, s = onQuay(a, c, z);
  s.c = c; s.fade = Math.min(1, u * 14, (1 - u) * 14); s.dirx = (cr.south ? 1 : -1) * (k === 1 ? -1 : 1);
  return s;
}
/** layer: 0 on the north quay, 1 on the bridge, 2 on our quay */
function drawCrossers(t, crowd, layer) {
  CROSSERS.forEach((cr, i) => {
    if (cr.rank >= crowd) return;
    for (const [k, lag] of cr.k2 ? [[cr.k, 0], [cr.k2, 0.018]] : [[cr.k, 0]]) {
      const s = crossAt(cr, t, lag);
      if (!s || (s.c > RW ? 0 : s.c >= 0 ? 1 : 2) !== layer) continue;
      const P = k(s.h), bro = k === cr.k ? (k.brolly || (cr.k2 ? null : BROLLIES[i % 8])) : (cr.k.brolly ? null : k.brolly);
      const draw = () => {
        ctx.globalAlpha = s.fade;
        const hy = person(s.x, s.y, P, { view: 2, dir: s.dirx, step: (walkF(t) + i) & 1 });
        if (bro) brolly(Math.round(s.x), hy, s.h, bro);
        ctx.globalAlpha = 1;
      };
      if (layer === 2) put(s.y, draw); else draw();
    }
  });
}
/* ----- two far figures at the boardwalk's rail, and the drinkers behind the pub's window ----- */
const BOARDERS = [{ a: 61, rank: 0.22, k: kit(180, { brolly: false }) }, { a: 66.5, rank: 0.7, k: kit(181, { brolly: false }) }];
function drawBoarders(t, crowd) {
  for (const b of BOARDERS) if (b.rank < crowd) { const s = onQuay(b.a, RW - BWALK.w + 0.5, BWALK.z); person(s.x, s.y, b.k(s.h), { view: 1, lean: hash(b.a | 0, step(t, 30), 5) < 0.3 ? 1 : 0 }); }
}
const SIL = '#4a2612';
function drawDrinkers(t) {
  [[34.4, 0], [36.3, 0.4], [32.6, 0.7]].forEach(([a, ph], i) => {
    const p = proj(a, CSF, QZ + 2.25), sc = F / p[2] / 9, X = Math.round(p[0]), nod = Math.round(0.6 * wave(t, 60 + i * 12, ph));
    const hw = Math.max(1, Math.round(1.2 * sc)), bot = Math.round(proj(a, CSF, QZ + 0.78)[1]), top = Math.round(p[1]) + nod;
    R(X - hw, top, hw * 2, Math.round(3 * sc), SIL); R(X - hw - 1, top + Math.round(3 * sc), hw * 2 + 2, Math.max(1, bot - top - Math.round(3 * sc)), SIL);
    if (i === 1) { const up = frac(t / CUP_T + 0.5) < 0.12; R(X + hw + 1, top + (up ? 1 : Math.round(4 * sc)), 1, 2, '#f0b040'); }   // a pint goes up
  });
}


/* ================= Soft rain (3 depths; finer and slower than London's), rings on the river ================= */
const RAIN_LIGHTS = [], RAIN_LIGHTS_BB = [];          // filled in prerender(): { x, y, r, r2 }
const WEATHER = weatherPlans(['rain']);
const WX_LAND = [[150, 196], [198, 262], [H + 4, H + 4]];
let WX, WX_BB;
function litAt(x, y, lights) {
  for (const l of lights) { const dx = x - l.x, dy = y - l.y; if (dx * dx + dy * dy < l.r2) return true; }
  return false;
}
const capRow = x => 161 + Math.max(0, x - 330) * 0.3;   // roughly where our quay wall's cap runs on screen
const RAIN = [
  { count: 150, nMin: 120, nMax: 150, fall: 190, ff: 1, len: 1, slope: 0.1, col: 'rgba(150,162,200,0.34)', warm: 'rgba(255,206,140,0.5)', splash: '#98a4c4',
    land: (x, a) => 150 + Math.floor(a * 46) },
  { count: 100, nMin: 150, nMax: 180, fall: 240, ff: 0.92, len: 2, slope: 0.13, col: 'rgba(170,180,214,0.4)', warm: 'rgba(255,208,146,0.62)', splash: '#a8b2d0',
    land: (x, a) => x > 345 ? Math.floor(capRow(x) + 4 + a * 30) : 198 + Math.floor(a * 66) },
  { count: 46, nMin: 190, nMax: 220, fall: 300, ff: 0.86, len: 4, slope: 0.18, col: 'rgba(200,208,236,0.5)', warm: 'rgba(255,216,156,0.75)', splash: '#c8d0e8',
    land: (x, a, b) => x > 372 && a < 0.7 ? Math.floor(capRow(x) + 22 + b * 60) : H + 6 },
];
RAIN.forEach((L, li) => {
  const r = rng(300 + li);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
function drawRain(L, t, lights) {
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -24 + hash(d.id, cyc, 11) * (W + 24 + L.fall * L.slope);
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12), hash(d.id, cyc, 13));
    if (u < L.ff) {
      const s = u / L.ff, yh = yl - L.fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1 || yh > H) continue;
      ctx.fillStyle = litAt(xh, yh, lights) ? L.warm : L.col;
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh - k * L.slope), Math.round(yh - k), 1, 1);
    } else if (yl < H) {
      const s = (u - L.ff) / (1 - L.ff), big = L.len > 3, hgt = Math.sin(Math.PI * s) * (big ? 2.2 : 1), spread = 1 + s * (big ? 2.2 : 1);
      ctx.globalAlpha = 1 - s;
      ctx.fillStyle = litAt(xl, yl, lights) ? L.warm : L.splash;
      const Y = Math.round(yl - 1 - hgt);
      ctx.fillRect(Math.round(xl - spread), Y, 1, 1); ctx.fillRect(Math.round(xl + spread), Y, 1, 1);
      ctx.globalAlpha = 1;
    }
  }
}
const RINGS = [];
{ const r = rng(81); for (let i = 0; i < 40; i++) RINGS.push({ id: i, n: 80 + Math.floor(r() * 40), ph: r() }); }
function drawRings(t) {
  ctx.fillStyle = '#93a0c6';
  for (const g of RINGS) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.5) continue;
    const s = u / 0.5, a = 17 + Math.pow(hash(g.id, cyc, 1), 1.6) * 90, p = proj(a, 1.5 + hash(g.id, cyc, 2) * 41, 0);
    const rx = (0.15 + s * 0.55) * F / p[2], ry = rx * clamp(EYE / p[2] * 1.4, 0.12, 0.6), pts = 5 + Math.floor(rx * 2.4);
    ctx.globalAlpha = (1 - s) * 0.5;
    for (let j = 0; j < pts; j++) { const an = j / pts * TAU; ctx.fillRect(Math.round(p[0] + Math.cos(an) * rx), Math.round(p[1] + Math.sin(an) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: the sky and what stands far down the river ================= */
function drawScroll(cv, y, k, t) {                     // the clouds come in from the west and south: right to left on screen
  const off = (W - scroll(t, k, W)) % W;
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawPlane(t) {                                // far off, on its way in to the airport north of the city
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(470 - u * 500), y = Math.round(22 + u * 26);
  R(x, y, 4, 1, '#232a42'); dot(x + 1, y - 1, '#232a42');
  halo(HL.planeLamp, x - 1, y, 0.5); dot(x - 1, y, '#fff8e8');
  if (frac(t / 1.5 + 0.3) < 0.5) dot(x + 3, y - 1, '#ff3a3a');
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) { const a = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph)); if (a > 0.28) { ctx.globalAlpha = a; R(s.x, s.y, 1, 1, '#c8d2ee'); } }
  const clear = 0.45 + 0.55 * smooth(14, 30, t) * (1 - smooth(62, 84, t));   // the moon comes out of its veil for a while
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.75 + 0.25 * (1 - clear) + 0.08 * wave(t, 4)); halo(HL.moonCore, MOON.x, MOON.y, clear);
  ctx.globalAlpha = 0.35 + 0.6 * clear; ctx.drawImage(MOON_C, MOON.x - 6, MOON.y - 6); ctx.globalAlpha = 1;
  drawScroll(CLOUDS[0].c, CLOUDS[0].y, CLOUDS[0].k, t);
  drawPlane(t);
  drawScroll(CLOUDS[1].c, CLOUDS[1].y, CLOUDS[1].k, t);
  drawScroll(CLOUDS[2].c, CLOUDS[2].y, CLOUDS[2].k, t);
}
function drawFog(t) {
  const D = clamp(FOG.dens(t), 0, 0.999) * FOG_LEVELS, k = Math.floor(D), fr = D - k, off = scroll(t, FOG.k, W);
  for (const [cv, a] of [[FOG_L.levels[k], 1 - fr], [FOG_L.levels[k + 1], fr]]) {
    if (a * FOG.alpha < 0.004) continue;
    ctx.globalAlpha = a * FOG.alpha;
    ctx.drawImage(cv, off - W, FOG.y); ctx.drawImage(cv, off, FOG.y);
  }
  ctx.globalAlpha = 1;
}
function clipBlit(cv, x, y, x0, x1, rows) {            // a sprite that shows only between columns x0 and x1
  const a = Math.max(x, x0), b = Math.min(x + cv.width, x1);
  if (b > a) ctx.drawImage(cv, a - x, 0, b - a, rows, a, y, b - a, rows);
}
function drawFar(t) {
  blit(FAR_C);
  for (const w of FAR_WINS) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) dot(w.x, w.y, w.col);
  const on = frac(t / 2) < 0.5;                        // aircraft warning lamps: the cranes, Liberty Hall
  CRANES.forEach((c, i) => { if (on === !(i & 1)) { dot(c.x, c.top - 4, '#ff3a3a'); halo(HL.red, c.x, c.top - 4, 0.7); } });
  if (!on) { dot(LIB.x0 + 5, LIB.top - 6, '#ff3a3a'); halo(HL.red, LIB.x0 + 5, LIB.top - 6, 0.6); }
  halo(HL.dome, DOME.cx, DOME.top + 4, 0.85 + 0.15 * wave(t, 6, 0.2));
  halo(HL.spire, SPIRE.x, SPIRE.tip + 3, 0.8 + 0.2 * wave(t, 8, 0.5));
  for (let i = 0; i < 9; i++) {                        // the roof sign on O'Connell Bridge House: bars of light that change slowly
    const v = hash(i, step(t, 60), 14);
    dot(OCH.x0 + 2 + i, OCH.top - 4 + (i % 3), v < 0.45 ? '#ffb040' : v < 0.8 ? '#ff5a48' : '#3a2a30');
  }
  halo(HL.sign, (OCH.x0 + OCH.x1) >> 1, OCH.top - 3, 0.8);
  for (const [a, b, dir] of DART) if (t >= a && t < b) {
    const u = (t - a) / (b - a), x = Math.round(dir > 0 ? 226 + u * 104 : 330 - u * 104);
    clipBlit(DART_C, x, LOOP_Y - 4, 266, 328, 3);
  }
  for (const b of OBUS) if (t >= b.t0 && t < b.t1) {
    const u = (t - b.t0) / (b.t1 - b.t0), span = OCB.x1 - OCB.x0 + OBUS_W, x = Math.round(b.dir > 0 ? OCB.x0 - OBUS_W + u * span : OCB.x1 - u * span);
    clipBlit(FARBUS[b.live][b.dir > 0 ? 0 : 1], x, OCB.deck - 8, OCB.x0 + 1, OCB.x1, 6);
  }
  OCB_LAMPS.forEach((x, i) => { dot(x, OCB.deck - 7, '#ffe9b4'); halo(HL.farLamp, x, OCB.deck - 7, 0.8 + 0.2 * wave(t, 400 + i * 37)); });
}

/* ================= Per-frame: the Liffey ================= */
const STREAKS = [];                                    // { x, y0, y1, w, col, a, seed }: filled in prerender()
function drawWater(t, rain) {
  blit(WBASE_C);
  ctx.globalAlpha = 0.7;
  for (let y = WATER_Y0; y < H; y++) {                 // the mirror, rocked by a slow swell: rows move in pairs, a little more the nearer they are
    const k = y - WATER_Y0, band = k >> 1;
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + band * 0.9) * Math.min(1.3, 0.3 + k * 0.014) + Math.sin(TAU * 110 * t / LOOP - band * 2.3) * Math.min(0.5, k * 0.008));
    ctx.drawImage(MIRROR_C, 0, y, W, 1, off, y, W, 1);
  }
  for (let y = 166, n = 0; y < H; y += 2, n++) for (let j = 0; j < 4; j++) {     // ripple troughs and glints, drifting down the river
    const k = 2 + ((n * 7 + j * 5) % 7), len = Math.round((3 + hash(n, j, 41) * 10) * (0.4 + (y - 160) / 60));
    const x = Math.round(frac(hash(n, j, 40) + (j & 1 ? 1 : -1) * k * t / LOOP) * (W + 40)) - 20;
    ctx.globalAlpha = j === 3 ? 0.2 : 0.4;
    R(x, y + (j & 1), len, 1, j === 3 ? '#8a94b8' : '#070a14');
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0, s.y1);
  for (const b of QBUS) {                              // a bus on the north quay lays its lit windows on the river
    const s = busAt(b, t);
    if (!s) continue;
    const p = proj(s.a + 5.5, RW, 0);
    if (p[0] > 2) streak(ctx, t, Math.round(p[0]), Math.max(2, Math.round(40 / p[2] * 6)), '#ffd890', 0.3 * s.fade, 9, Math.round(p[1]) + 2, Math.min(H, Math.round(p[1]) + 34));
  }
  if (rain) drawRings(t);
}
function drawCormorant(t) {
  const n = Math.floor(t / DIVE_T), v = t - n * DIVE_T, spots = [[58, 17], [63, 22], [55, 26], [66, 15], [60, 20], [57, 23]];
  const [a0, c0] = spots[n % 6], [a1, c1] = spots[(n + 1) % 6];
  if (v > 26 && v < 33) {                              // under: only a ring where it went down
    const p = proj(a0, c0, 0), s = (v - 26) / 2.2;
    if (s < 1) { ctx.globalAlpha = (1 - s) * 0.7; const rx = 1 + s * 4; for (let j = 0; j < 10; j++) dot(p[0] + Math.cos(j / 10 * TAU) * rx, p[1] + Math.sin(j / 10 * TAU) * rx * 0.3, '#aab6d8'); ctx.globalAlpha = 1; }
    return;
  }
  const up = v >= 33, a = up ? a1 : a0 + 0.5 * Math.sin(v * 0.3), c = up ? c1 : c0 + v * 0.05, p = proj(a, c, 0);
  ctx.drawImage(CORM_C[hash(9, step(t, 80), 6) < 0.5 ? 0 : 1], Math.round(p[0]) - 2, Math.round(p[1]) - 4);
  ctx.globalAlpha = 0.3; R(Math.round(p[0]) - 3, Math.round(p[1]) + 1, 6, 1, '#070a14'); ctx.globalAlpha = 1;
}

/* ================= Per-frame: the north quay, the bridge, our quay ================= */
function drawWins(t, north) {
  for (const w of WINS) {
    if (w.north !== north || w.x1 < 0) continue;
    if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < w.p) ctx.drawImage(north ? NLIT_C : SLIT_C, w.x0, w.y0, w.x1 - w.x0 + 1, w.y1 - w.y0 + 1, w.x0, w.y0, w.x1 - w.x0 + 1, w.y1 - w.y0 + 1);
  }
}
const sigState = t => { const u = frac(t / 60); return u < 0.42 ? 0 : u < 0.47 ? 1 : u < 0.95 ? 2 : 1; };   // the green man, amber, the red man, amber
const SIG_COL = ['#3ae07a', '#ffb040', '#ff4a3a'];
function drawSignal(q, t, hl) { const s = sigState(t); dot(q[0], q[1] + (s === 0 ? 2 : s === 1 ? 1 : 0), SIG_COL[s]); halo(hl[s], q[0], q[1] + 1, 0.8); }
function drawNorth(t, crowd) {
  blit(NORTH_C);
  drawWins(t, true);
  for (const b of QBUS) { const s = busAt(b, t); if (s) { ctx.globalAlpha = s.fade; drawQuayVehicle(s.a, BUS_V, LIVERY[b.live], t, s.braking, s.blink); ctx.globalAlpha = 1; } }
  if (t >= TAXI.t0 && t < TAXI.t1) { const u = (t - TAXI.t0) / (TAXI.t1 - TAXI.t0); ctx.globalAlpha = 1 - smooth(0.9, 1, u); drawQuayVehicle(lerp(40, 204, Math.pow(u, 1.5)), TAXI_V, TAXI_L, t, false, false); ctx.globalAlpha = 1; }
  drawCrossers(t, crowd, 0);
  blit(NWALL_C);
  N_LAMPS.forEach((l, i) => halo(l.d < 110 ? HL.qLamp : HL.farLamp, l.x, l.y, 0.85 + 0.15 * wave(t, 500 + i * 29)));
  BLADES.forEach((b, i) => { if (i < 2) halo(HL.blade[i], b.x, b.y, 0.7 + 0.1 * wave(t, 30, i * 0.3)); });
  drawSignal(SIG.n, t, HL.sig);
  drawBoarders(t, crowd);
  for (const tr of TREES) for (let k = 0; k < 7; k++) {   // a few leaves turning in the wind
    if (hash(k, step(t, 240), tr.x) < 0.5) dot(tr.x + (hash(k, 1, tr.x) - 0.5) * tr.rx * 1.6, tr.y + (hash(k, 2, tr.x) - 0.3) * tr.ry, '#c8843a');
  }
}
const TAXI_L = { body: '#20242e', band: '#20242e', rear: '#181b24', roof: '#2a2e3a' };
function drawBridge(t, crowd) {
  blit(BR_BACK);
  drawCrossers(t, crowd, 1);
  blit(BR_FRONT);
  BR_LAMPS.forEach((l, i) => { halo(HL.brLamp, l.x, l.y, 0.85 + 0.15 * wave(t, 420 + i * 31)); halo(HL.brGlow, l.x, l.y + 6, 0.8); });
}
const SMOKE = [];
{ const r = rng(63); for (let i = 0; i < 16; i++) SMOKE.push({ n: 24 + Math.floor(r() * 8), ph: r(), k: 14 + Math.floor(r() * 16), p: r() }); }
const CAUSTICS = [];
{ const r = rng(64); for (let i = 0; i < 70; i++) CAUSTICS.push({ a: 9 + Math.pow(r(), 1.5) * 34, z: 0.3 + r() * 2.6, k: 30 + Math.floor(r() * 50), ph: r(), len: 0.4 + r() * 0.9 }); }
function drawSouth(t, crowd, flickLv) {
  blit(SOUTH_C);
  drawWins(t, false);
  const door = t - Math.floor(t / DOOR_T) * DOOR_T, open = crowd > 0 ? smooth(0.2, 1.2, door) * (1 - smooth(3.2, 4.4, door)) : 0;
  const bar = 0.82 + 0.18 * wave(t, 90, 0.2) * (hash(2, step(t, 480), 8) < 0.9 ? 1 : 0.3);
  halo(HL.blade[2], BLADES[2].x, BLADES[2].y, bar); drawPool(HL.poolBar, BAR_POOL.x, BAR_POOL.y, 0.7 * bar);
  halo(HL.pub, PUB_GLOW.x, PUB_GLOW.y, 0.9 + 0.1 * wave(t, 10, 0.4)); halo(HL.arch, ARCH_GLOW.x, ARCH_GLOW.y, 0.8);
  drawPool(HL.poolPub, PUB_GLOW.x - 26, PUB_GLOW.y + 13, 0.85); drawPool(HL.poolDoor, DOOR_GLOW.x - 8, DOOR_GLOW.y + 2, 0.4 + 0.6 * open);
  if (open > 0.02) { ctx.globalAlpha = open; R(DOOR_GLOW.x - 1, DOOR_GLOW.y - 11, 3, 11, '#ffe6b0'); ctx.globalAlpha = 1; halo(HL.door, DOOR_GLOW.x, DOOR_GLOW.y - 6, open); }
  for (const s of GROUND_STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0, s.y1);
  if (crowd > 0) drawDrinkers(t);
  BASKETS.forEach((b, i) => {                           // the pub's hanging baskets stir
    const dx = Math.round(0.7 * wave(t, 26, i * 0.31) + 0.4 * wave(t, 61, i * 0.2));
    R(b.x, b.y - 3, 1, 3, '#14161f'); ctx.drawImage(BASKET_C, b.x - 2 + dx, b.y);
  });
  drawSignal(SIG.s, t, HL.sig);
  for (const s of SMOKE) {                              // turf smoke from the bar's chimney, taken north-east by the wind
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = SMOKE_AT.x - u * u * 38 - u * 10 + wave(t, s.k, s.p) * 2 * u, y = SMOKE_AT.y - u * 34 + u * u * 8, rr = Math.min(5, 1 + Math.floor(u * 5));
    ctx.globalAlpha = 0.5 * Math.min(1, u * 7) * Math.pow(1 - u, 1.3);
    ctx.drawImage(PUFFS[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
  DL = [];
  if (crowd > 0) { drawCast(t, crowd); drawWalkers(t, crowd); drawRiders(t, crowd); drawCrossers(t, crowd, 2); }
  DL.sort((p, q) => p.y - q.y);
  for (const p of DL) p.f();
  blit(SWALL_C);
  for (const c of CAUSTICS) {                           // the river's light playing on our quay wall
    const v = wave(t, c.k, c.ph);
    if (v < 0.25) continue;
    const a = c.a + 0.6 * wave(t, 7, c.ph), z = c.z + 0.15 * wave(t, c.k >> 1, c.ph * 3), p = proj(a, 0, z), q = proj(a + c.len, 0, z);
    ctx.globalAlpha = (v - 0.25) * 0.62;
    dot(p[0], p[1], '#8fa6c8'); dot((p[0] + q[0]) / 2, (p[1] + q[1]) / 2, '#a9bedc'); dot(q[0], q[1], '#8fa6c8');
  }
  ctx.globalAlpha = 1;
  for (let k = 1, n = OUTFALL.wl - OUTFALL.y; k < n; k++) if (hash(k, step(t, 960), 12) < 0.62) dot(OUTFALL.x + 1 + (k > n * 0.6 ? 1 : 0), OUTFALL.y + k, k > n - 3 ? '#c8d4f0' : '#7f95bc');   // the drain's trickle
  S_LAMPS.forEach((l, i) => halo(l.d < 110 ? HL.qLamp : HL.farLamp, l.x, l.y, 0.85 + 0.15 * wave(t, 520 + i * 23)));
  const li = (0.95 + 0.05 * wave(t, 1500, 0.3)) * flickLv;
  if (li < 0.9) R(LAMP_NEAR.x - 3, LAMP_NEAR.y - 4, 7, 10, `rgba(20,14,10,${(0.9 - li).toFixed(3)})`);
  halo(HL.lamp, LAMP_NEAR.x, LAMP_NEAR.y, li); halo(HL.lampCore, LAMP_NEAR.x, LAMP_NEAR.y, li);
  drawPool(HL.poolLamp, LAMP_NEAR.x + 22, LAMP_NEAR.base + 16, 0.7 * li);
}
function drawNearGull(t) {
  const cry = NEAR_GULL_CRIES.some(([a, b]) => t >= a && t < b) && frac(t * 1.6) < 0.6;
  const h = hash(step(t, 40), 5, 71), pose = cry ? 2 : h < 0.2 ? 1 : 0, turned = !cry && h > 0.82, { x, y } = NEAR_GULL;
  ctx.globalAlpha = 0.3; R(x - 6, y + 1, 12, 1, '#0a0c16'); ctx.globalAlpha = 1;                   // its shadow on the wet stone
  ctx.drawImage(NEAR_GULL_C[pose][turned ? 1 : 0], x - 9, y - 11);
}
function drawGulls(t) {
  drawNearGull(t);
  const flying = t >= FLOCK.t0 && t < FLOCK.t1;
  for (const b of GULLS) {
    const s = flying ? clamp(((t - FLOCK.t0) / (FLOCK.t1 - FLOCK.t0) - b.del) / (1 - 2 * b.del), 0, 1) : 0;
    if (s <= 0 || s >= 1) {                              // perched: now and then it turns round
      const f = (hash(b.i, step(t, 60), 8) < 0.5) !== b.flip;
      if (b.big) ctx.drawImage(GULL_C[f ? 1 : 0], b.x - 3, b.y - 6);
      else { R(b.x - 1, b.y - 2, 3, 2, '#dfe4f0'); dot(b.x + (f ? -1 : 1), b.y - 3, '#f2f4fa'); }
      continue;
    }
    const e = s < 0.5 ? Math.sqrt(Math.sin(Math.PI * s)) : Math.pow(Math.sin(Math.PI * s), 1.5), th = b.phi + TAU * b.laps * s;
    const x = Math.round(b.x + (b.rx * (Math.cos(th) - Math.cos(b.phi)) + b.bx) * e), y = Math.round(b.y - 4 + (b.ry * (Math.sin(th) - Math.sin(b.phi)) - b.by) * e);
    const glide = s > 0.15 && s < 0.85 && hash(b.i, step(t, 480), 9) < 0.45;
    ctx.drawImage(GULL_F[glide ? 1 : Math.floor(t * 8 + b.i * 0.7) % 3], x - 3, y - 1);
  }
  for (const [a, b, y0, dir] of [[18, 34, 46, 1], [214, 232, 70, -1]]) if (t >= a && t < b) {   // a lone gull crosses the sky
    const u = (t - a) / (b - a), x = Math.round(dir > 0 ? -8 + u * 300 : W + 8 - u * 330), y = Math.round(y0 + 14 * Math.sin(u * 4) - u * 12);
    ctx.drawImage(GULL_F[hash(4, step(t, 480), 3) < 0.4 ? 1 : Math.floor(t * 7) % 3], x - 3, y - 1);
  }
}


/* ================= The hoarding, per frame: glow in the rain, the frame, the face (own ad or the pitch), the lamps ================= */
const BILL_FLICK = [[76.4, 77.9]];                    // the right-hand lamp sputters once a loop
function drawAd(t, lv) {
  ctx.drawImage(ADS[0], BILL.x, BILL.y);
  if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(ADS[1], BILL.x, BILL.y); ctx.globalAlpha = 1; }
  for (let k = 0; k < 2; k++) {                        // two notes off the fiddle's strings
    const v = frac(t / 6 + k * 0.5), x = BILL.x + 12 + k * 2 + Math.round(Math.sin(v * 6 + k * 2) * 1.5), y = BILL.y + 11 - Math.round(v * 9);
    if (v > 0.85) continue;
    R(x, y, 2, 1, '#194d36'); R(x + 1, y - 2, 1, 2, '#194d36');
  }
}
function drawHoarding(t, pitch) {
  const lv = flick(t, BILL_FLICK, 13);
  halo(HL.billGlow, BILL.x + (BILL.w >> 1), BILL.y + 2, (0.9 + 0.1 * wave(t, 8, 0.2)) * (2 + lv) / 3);
  blit(HOARD_C);
  if (!PITCH.covers(t, pitch)) drawAd(t, lv);
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
  BILL_LAMPS.forEach((lx, i) => {
    const a = i === 2 ? lv : 1;
    if (a < 1) R(lx - 2, BILL_LAMP_Y + 2, 5, 1, `rgba(40,26,18,${(0.85 * (1 - a)).toFixed(3)})`);
    halo(HL.billLamp, lx, BILL_LAMP_Y + 3, a * (0.92 + 0.08 * wave(t, 700 + i * 37)));
  });
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR_C, FOG_L, NORTH_C, NWALL_C, SOUTH_C, SWALL_C, NLIT_C, SLIT_C, MIRROR_C, WBASE_C, BR_BACK, BR_FRONT,
  VIG_C, PITCH, HOARD_C, ADS, GULL_C, GULL_F, CORM_C, DART_C, FARBUS, PUFFS, NEAR_GULL_C;
let PUB_GLOW, DOOR_GLOW, ARCH_GLOW, BAR_POOL, SMOKE_AT, BASKETS, BASKET_C;
const GROUND_STREAKS = [];
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [6, 8, 16], Math.round(q * 0.45 * 255));
  }
  return p.done();
}
const at = (a, c, z) => { const p = proj(a, c, z); return { x: Math.round(p[0]), y: Math.round(p[1]), d: p[2] }; };
function prerender() {
  HL = {
    moon: makeHalo(44, '#8a96c4', 0.2, 1.8), moonCore: makeHalo(13, '#dfe4f6', 0.32, 1.5),
    red: makeHalo(4, '#ff3030', 0.6, 1), dome: makeHalo(16, '#9ad0b4', 0.16, 1.5), spire: makeHalo(7, '#cfe0ff', 0.4, 1.3), sign: makeHalo(9, '#ff8a48', 0.25, 1.4),
    planeLamp: makeHalo(6, '#fff4d0', 0.5, 1.2), farLamp: makeHalo(4, '#ffd898', 0.42, 1.2), qLamp: makeHalo(8, '#ffd490', 0.36, 1.3),
    brLamp: makeHalo(9, '#ffe2a8', 0.5, 1.3), brGlow: makeHalo(22, '#ffc880', 0.13, 1.6),
    lamp: makeHalo(58, '#ffb866', 0.24, 1.7), lampCore: makeHalo(16, '#ffe6b0', 0.5, 1.4),
    tail: makeHalo(4, '#ff3a30', 0.5, 1), win: makeHalo(12, '#ffb45a', 0.16), boat: makeHalo(14, '#ffc27a', 0.24), bikeLamp: makeHalo(7, '#f4f8ff', 0.5, 1.2),
    blade: [makeHalo(13, '#6a8ae0', 0.2, 1.5), makeHalo(11, '#5ad09a', 0.18, 1.5), makeHalo(15, '#ff4a5c', 0.3, 1.5)],
    sig: [makeHalo(5, '#3ae07a', 0.5, 1.2), makeHalo(5, '#ffb040', 0.5, 1.2), makeHalo(5, '#ff4a3a', 0.5, 1.2)],
    pub: makeHalo(40, '#ffac58', 0.2, 1.6), arch: makeHalo(16, '#ffb060', 0.22, 1.5), door: makeHalo(14, '#ffe0a0', 0.3, 1.4),
    billGlow: makeHalo(30, '#ffc878', 0.09, 1.6), billLamp: makeHalo(6, '#ffe0a0', 0.3, 1.3),   // small lamps: under the bridge lanterns' glow
    poolPub: makePool(34, 9, '#ffb060', 0.3), poolDoor: makePool(12, 4, '#ffd088', 0.4), poolBar: makePool(14, 4, '#ff5a6a', 0.26),
    poolLamp: makePool(40, 14, '#ffb866', 0.3), poolBike: makePool(9, 3, '#dfe8ff', 0.3),
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FOG_L = makeFog(FOG);
  const farP = pix(W, H);
  FAR_C = renderFar(farP);
  const q = renderQuays(farP);
  decorateQuays(q.N, q.NW, q.S, q.SW, q.NL, q.SL);
  renderNearLamp(q.SW);
  const bb = pix(W, H), bf = pix(W, H), mb = pix(W, H), mf = pix(W, H);
  paintBridge(bb, bf, false); paintBridge(mb, mf, true);
  for (const m of [mb, mf]) for (let i = 0; i < W * H; i++) if (m.d[i * 4 + 3] && WMASK[i]) {
    const o = i * 4; q.M.d[o] = m.d[o]; q.M.d[o + 1] = m.d[o + 1]; q.M.d[o + 2] = m.d[o + 2]; q.M.d[o + 3] = 255;
  }
  NORTH_C = trim(q.N); NWALL_C = trim(q.NW); SOUTH_C = trim(q.S); SWALL_C = trim(q.SW); NLIT_C = q.NL.done(); SLIT_C = q.SL.done();
  MIRROR_C = q.M.done(); WBASE_C = trim(q.WB);
  BR_BACK = trim(bb); BR_FRONT = trim(bf);
  HOARD_C = renderHoarding(); ADS = [renderAd(false), renderAd(true)];
  PITCH = makePitch(BILL.w, BILL.h, { accent: '#3984c6' });
  GULL_C = [sprite(GULL_SIT, GULL_KEY), sprite(GULL_SIT, GULL_KEY, true)];
  GULL_F = GULL_FLY.map(f => sprite(f, GULL_KEY));
  NEAR_GULL_C = NEAR_GULL_HEADS.map(hd => { const rows = [...hd.slice(0, 4), ...NEAR_GULL_BODY]; if (hd[4]) rows[4] = hd[4]; return [sprite(rows, NEAR_GULL_KEY), sprite(rows, NEAR_GULL_KEY, true)]; });
  CORM_C = [sprite(CORM, CORM_KEY), sprite(CORM, CORM_KEY, true)];
  DART_C = renderDart(); FARBUS = LIVERY.map(L => [renderFarBus(L, false), renderFarBus(L, true)]);
  PUFFS = [];
  for (let pr = 1; pr <= 5; pr++) {
    const s = pr * 2 + 1, p = pix(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) { const d = Math.hypot(i - pr, y - pr) / pr; if (d < 1 && (1 - d) * 1.6 > bayer(i, y) * 0.9) p.set(i, y, y < pr * 0.7 ? '#aaa8b8' : '#84849e'); }
    PUFFS[pr] = p.done();
  }
  VIG_C = renderVignette();
  BASKET_C = sprite(['.fgf.', 'gfgpg', 'gkkkg', '.gkg.'], { f: '#e8508a', p: '#f0c040', g: '#2e6a3a', k: '#3a2a20' });
  BASKETS = [32.3, 34.7, 37.2].map(a => at(a, CSF + 0.6, QZ + 2.95));
  // anchors of light on our quay
  PUB_GLOW = at(34.6, CSF, QZ + 1.9); DOOR_GLOW = at(39.15, CSF, QZ); ARCH_GLOW = at(HALL.a0 + 4.9, CSF, QZ + 1.7);
  BAR_POOL = at(52.4, CSF + 2.4, QZ); SMOKE_AT = at(BAR.a0 + 0.6, CSF - 0.5, QZ + BAR.top + 1.9);
  // the lamps' long streaks on the river: under every quay lamp of the north wall, under the bridge's three lanterns
  N_LAMPS.forEach((l, i) => {
    const w0 = proj(l.a, RW, 0), m = proj(l.a, RW + 0.22, -(CAPZ + 4.2));
    STREAKS.push({ x: l.x, y0: Math.round(w0[1]) + 1, y1: Math.min(H, Math.round(m[1]) + 16), w: l.d < 90 ? 3 : 2, col: '#ffd898', a: l.d < 110 ? 0.4 : 0.26, seed: 3 + i });
  });
  BR_LAMPS.forEach((l, i) => {
    const m = proj(AB, l.c, -(deckZ(l.c) + RAILH + ARCH_H - 0.45));
    STREAKS.push({ x: l.x, y0: Math.round(m[1]) - 14, y1: Math.min(H, Math.round(m[1]) + 22), w: 4, col: '#ffe6b0', a: 0.4, seed: 20 + i });
  });
  STREAKS.push({ x: MOON.x, y0: 226, y1: H, w: 6, col: '#c8d4f0', a: 0.2, seed: 33 });               // and the moon's glitter, low on the river
  // the open shops of Bachelors Walk: the mirrored quay wall hides their fronts, but their light still lies on the river,
  // from the foot of the wall out towards us, under where their mirror image would be
  const waterTop = x => { for (let y = WATER_Y0; y < H; y++) if (WMASK[y * W + x]) return y; return H; };
  N_HOUSES.forEach((s, i) => {
    if (!s.open || s.a0 > 125) return;
    const m = proj((s.a0 + s.a1) / 2, CNF, -(QZ + 1.8)), x = Math.round(m[0]);
    if (x < 2 || x > W - 3) return;
    const y0 = waterTop(x);
    if (y0 < H - 8) STREAKS.push({ x, y0: y0 + 1, y1: Math.min(H, Math.round(m[1]) + 8 + Math.round(hash(i, 2, 61) * 8)), w: clamp(Math.round(F * 1.1 / m[2]), 2, 5),
      col: '#ffc878', a: (m[2] < 90 ? 0.24 : 0.17) + 0.08 * hash(i, 1, 61), seed: 31 + i });
  });
  // the windows the river mirrors: each run of lit columns lays one streak (runs a pixel or two apart are one), the strongest ten
  const runs = [];
  for (let x = 0; x < W; x++) {
    if (!q.LIT[x].n) continue;
    const r = { x0: x, x1: x + 1, n: 0, y0: H, y1: -1, c: [0, 0, 0] };
    for (let gap = 0; x < W && gap < 3; x++) {
      const L = q.LIT[x];
      if (!L.n) { gap++; continue; }
      gap = 0; r.n += L.n; r.y0 = Math.min(r.y0, L.y0); r.y1 = Math.max(r.y1, L.y1); r.c[0] += L.r; r.c[1] += L.g; r.c[2] += L.b; r.x1 = x + 1;
    }
    x = r.x1;
    runs.push(r);
  }
  runs.sort((a, b) => b.n - a.n).slice(0, 10).forEach((r, i) => STREAKS.push({ x: (r.x0 + r.x1 - 1) >> 1, w: clamp(r.x1 - r.x0, 2, 5),
    col: toHex(mixc(r.c.map(v => v / r.n), [255, 212, 146], 0.55)), a: 0.2 + 0.14 * Math.min(1, r.n / 40), seed: 50 + i, y0: r.y0 - 1, y1: Math.min(H, r.y1 + 10) }));
  // warm lights lying on the wet road: the pub's window, Merchant's Arch, the bar's red sign
  for (const [a, col, al] of [[32.4, '#ffc878', 0.26], [34.8, '#ffc878', 0.3], [37, '#ffc878', 0.26], [HALL.a0 + 4.9, '#ffb868', 0.3], [52.4, '#ff5a6a', 0.26]]) {
    const p0 = at(a, CSF + 3.4, QZ), p1 = at(a, CSF + 8.6, QZ);
    GROUND_STREAKS.push({ x: (p0.x + p1.x) >> 1, y0: p0.y, y1: Math.min(H, p1.y + 1), w: Math.max(2, Math.round(26 / p0.d * 4)), col, a: al, seed: 40 + a });
  }
  // what the rain catches the light of
  RAIN_LIGHTS.push({ x: LAMP_NEAR.x, y: LAMP_NEAR.y, r: 44 }, { x: PUB_GLOW.x, y: PUB_GLOW.y, r: 30 },
    ...BR_LAMPS.map(l => ({ x: l.x, y: l.y, r: 13 })), ...N_LAMPS.slice(0, 3).map(l => ({ x: l.x, y: l.y, r: 9 })), ...S_LAMPS.slice(0, 2).map(l => ({ x: l.x, y: l.y, r: 9 })));
  RAIN_LIGHTS_BB.push(...RAIN_LIGHTS, { x: BILL.x + (BILL.w >> 1), y: BILL.y - 2, r: 20 });
  for (const l of RAIN_LIGHTS_BB) l.r2 = l.r * l.r;
  WX = { land: WX_LAND, lights: RAIN_LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
  WX_BB = { land: WX_LAND, lights: RAIN_LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the hoarding; billboard: false leaves the hoarding out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own soft rain when left out; crowd: the share of the
// people to show, 0..1 (CROWD when left out or not a number), 0 = nobody
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  const boat = boatAt(t), boatFar = boat && boat.a > AB - BW;
  drawSky(t);
  drawFar(t);
  if (billboard) drawHoarding(t, pitch);                // on the roofs of Bachelors Walk: the house fronts hide its feet
  if (wx.rain) drawRain(RAIN[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawWater(t, wx.rain);
  if (boatFar) drawBoat(boat, t);
  drawCormorant(t);
  drawNorth(t, crowd);
  drawFog(t);
  drawBridge(t, crowd);
  if (boat && !boatFar) drawBoat(boat, t);
  if (wx.rain) drawRain(RAIN[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawSouth(t, crowd, flick(t, LAMP_FLICK, 11));
  drawGulls(t);
  if (wx.rain) drawRain(RAIN[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'dublin',
  name: 'Dublin',
  tagline: 'Soft rain on the Liffey',
  timeZone: 'Europe/Dublin',
  accent: '#3984c6',                                   // Dublin blue, written out: tools/seo/seo.mjs reads it from the source
  focusX: Math.round(proj(AB, RW / 2, deckZ(RW / 2))[0]),
  landmark: [Math.floor(proj(AB - BW, RW, QZ)[0]), Math.ceil(proj(AB - BW, 0, QZ)[0])],   // the Ha'penny Bridge, quay wall to quay wall
  landmarkTop: Math.round(proj(AB, ARCH_C[1], deckZ(ARCH_C[1]) + RAILH + ARCH_H)[1]),      // the row of the middle lamp arch's crown
  billboard: BILL,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { rain: 0.34, water: 0.3, traffic: 0.2, crowd: 0.12, wind: 0.1 },
  events: [
    { t: 20, type: 'gull', x: 80, gain: 0.4 },
    { t: BOAT.t0 + 1, type: 'boat', dur: 46, pan: [-0.3, 0.15], gain: 0.4 },
    { t: NEAR_GULL_CRIES[0][0] + 0.1, type: 'gull', x: NEAR_GULL.x, gain: 0.32 },   // the gull on our coping cries
    ...BELLS.map(t => ({ t, type: 'bell', x: 430, gain: 0.45 })),   // Christ Church, up the hill behind us and to the right
    { t: FLOCK.t0 + 2.5, type: 'gull', x: 200, gain: 0.5 },
    { t: 215, type: 'horn', x: 110, gain: 0.22 },                   // a bus pulls out, far off on the north quay
    { t: 218, type: 'gull', x: 400, gain: 0.35 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
